/**
 * ============================================================================
 * SLIDE-OUT CART DRAWER CONTROLLER (z__jsCart.js)
 * ============================================================================
 * 
 * Handles all JavaScript functionality for both the slide-out cart drawer (cart.liquid)
 * and the full-page cart (/cart page via cart__main.liquid).
 * 
 * Features:
 * - Cart drawer show/hide/toggle
 * - Add to cart functionality (legacy and modern)
 * - Cart content rendering with items, upsells, pricing
 * - Free shipping progress bar
 * - Bundle item removal (API-nested and visually-nested)
 * - Quantity updates within the drawer
 * - Cart attributes (Clarity tracking, Bundle Product flag)
 * - Gift message/cart notes
 * 
 * Dependencies:
 * - jQuery (for legacy support and section data)
 * - Shopify.formatMoney (from Shopify's global JS)
 * - window.PXUTheme.getSectionData
 * 
 * Related Files:
 * - sections/cart.liquid - The slide-out cart section template (always loaded)
 * - sections/cart__main.liquid - The full-page cart section (/cart page)
 * 
 * @author SURI Development Team
 * @version 2.0.0 - January 2026
 * ============================================================================
 */

window.PXUTheme = window.PXUTheme || {};
var __webpack_exports__ = {};

window.PXUTheme.jsCart = {
  init: function ($section) {
    window.PXUTheme.jsCart = $.extend(this, window.PXUTheme.getSectionData($section));

    // Detect if we're on the /cart page (cart-page container exists in cart__main.liquid)
    this.isCartPage = !!document.querySelector(".cart-page");

    if (this.isCartPage) {
      // Full-page cart mode: scope all selectors to the .cart-page container
      const page = document.querySelector(".cart-page");
      this.cartEl = page;
      this.cartContainer = page.querySelector(".cart__container");
      this.cartContent = page.querySelector(".cart__content");
      this.cartEmpty = page.querySelector(".cart__empty");
      this.cartItemCount = page.querySelector("[data-cart-item-count]");
      this.cartItemContent = page.querySelector("[data-cart-content]");
      this.checkoutButton = page.querySelector("[data-cart-checkout]");
      this.cartUpsells = page.querySelector("[data-cart-upsells]");
      this.freeShippingEl = page.querySelector("[data-cart-free-shipping]");
      this.cartFooter = page.querySelector("[data-cart-footer]");
      this.cartNote = page.querySelector("[data-cart-note]");
    } else {
      // Drawer cart mode: use existing global selectors
      this.cartEl = document.getElementById("cart");
      this.cartContainer = document.querySelector(".cart__container");
      this.cartContent = document.querySelector(".cart__content");
      this.cartEmpty = document.querySelector(".cart__empty");
      this.cartItemCount = document.querySelector("[data-cart-item-count]");
      this.cartItemContent = document.querySelector("[data-cart-content]");
      this.checkoutButton = document.querySelector("[data-cart-checkout]");
      this.cartUpsells = document.querySelector("[data-cart-upsells]");
      this.freeShippingEl = document.querySelector("[data-cart-free-shipping]");
      this.cartFooter = document.querySelector("[data-cart-footer]");
      this.cartNote = document.querySelector("[data-cart-note]");
    }

    // Global selectors (shared between both modes)
    this.cartIconCount = document.querySelector(".cart-count");
    this.moneyFormat = document.querySelector("body").dataset.moneyFormat;
    this.cart = {};
    this.country = null;
    this.showYMAL = false;
    this.html = document.querySelector("html");

    const checkCountry = () => {
      this.country = document.body.getAttribute("data-fetched-country");
      if (this.country) {
        // Core functionality (both modes)
        this.updateCart();
        this.watchAtcButtons();
        this.watchBundleRevertButtons();
        this.watchLegacyAtcButtons();
        this.watchCheckoutButton();
        this.addFooterText();
        this.watchCartNote();

        // Drawer-only functionality (skip on cart page)
        if (!this.isCartPage) {
          this.toggleListeners();
          this.watchBackground();
          this.interceptCartNavigation();
        }
      } else {
        setTimeout(checkCountry, 100); // Retry every 100ms
      }
    };
    checkCountry();
  },

  /* ========================================================================
   * CART BACKGROUND OVERLAY
   * ======================================================================== */

  watchBackground() {
    if (this.isCartPage) return; // No background overlay on cart page
    document.querySelector(".cart__background").addEventListener("click", () => {
      this.hideCart();
    });
  },

  /* ========================================================================
   * CART NAVIGATION INTERCEPTION (NEW - January 2026)
   * ========================================================================
   * Intercepts navigation to /cart and opens the slide-out cart instead.
   * This provides a better UX by keeping users on their current page.
   * ======================================================================== */

  /**
   * Intercept clicks on /cart links and direct URL navigation
   * Opens slide-out cart instead of navigating to cart page
   */
  interceptCartNavigation() {
    // On the cart page, we don't intercept navigation - user is already viewing the full cart
    if (this.isCartPage) return;

    const self = this;

    // Intercept clicks on links to /cart
    document.addEventListener("click", (e) => {
      const link = e.target.closest('a[href*="/cart"]');
      if (link) {
        const href = link.getAttribute("href");
        // Check if this is a direct link to /cart (not /cart/add, /cart/change, etc.)
        if (href === "/cart" || href === "/cart/" || href.match(/^\/cart\/?(\?.*)?$/)) {
          e.preventDefault();
          e.stopPropagation();
          self.updateCart();
          self.showCart();
        }
      }
    }, true);

    // Handle browser back/forward navigation
    window.addEventListener("popstate", (e) => {
      if (e.state && e.state.cartOpen) {
        self.updateCart();
        self.showCart();
      }
    });
  },

  calculateFreeShipping() {
    let freeShippingAmount = 0;
    if (this.country == "United Kingdom") {
      freeShippingAmount = this.free_shipping_amount_uk;
    }
    if (this.country == "United States") {
      freeShippingAmount = this.free_shipping_amount_us;
    }
    if (freeShippingAmount > 0 && this.cart.total_price < freeShippingAmount) {
      let shippingWidth = (this.cart.total_price / freeShippingAmount) * 100;
      document.querySelector(":root").style.setProperty("--free-shipping-width", `${shippingWidth}%`);
      this.freeShippingEl.querySelector("span").innerHTML = `<strong>${this.formatter(freeShippingAmount - this.cart.total_price)}</strong> away from free delivery`;

      // Uncomment to re-enable free shipping header thing
      // this.freeShippingEl.classList.remove('hidden');
    }
    if (freeShippingAmount > 0 && this.cart.total_price >= freeShippingAmount) {
      let shippingWidth = (this.cart.total_price / freeShippingAmount) * 100;
      document.querySelector(":root").style.setProperty("--free-shipping-width", `${shippingWidth}%`);
      this.freeShippingEl.querySelector("span").innerHTML = "You’ve qualified for Free Delivery!";

      // Uncomment to re-enable free shipping header thing
      // this.freeShippingEl.classList.remove('hidden');
    }
    if (freeShippingAmount == 0) {
      this.freeShippingEl.classList.add("hidden");
    }
    if (this.cart.items) {
      if (this.cart.items.length == 0) {
        this.freeShippingEl.classList.add("hidden");
      }
    }
  },

  watchLegacyAtcButtons() {
    const legacyButtons = document.querySelectorAll(".ajax-submit");
    legacyButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const addToCartForm = event.target.closest("form");
        this.legacyAddToCart(addToCartForm);
      });
    });
  },

  legacyAddToCart(addToCartForm) {
    // Wrap addToCartForm in jQuery
    const $addToCartForm = $(addToCartForm);
    this.cartEl.classList.add("disabled");
    const $addToCartBtn = $addToCartForm.find(".button--add-to-cart");
    $addToCartForm.removeClass("shopify-product-form--unselected-error");
    let $continue = true;

    if (document.querySelector("body").classList.contains("product-2-1")) {
      if ($addToCartBtn[0].closest(".purchase-details").dataset.selectionStep === "false") {
        $continue = false;
      }
    }

    if ($continue == true) {
      if ($addToCartBtn[0].hasAttribute("data-options-unselected")) {
        const cartWarning = `<p class="cart-warning__message animated bounceIn">${window.PXUTheme.translation.select_variant}</p>`;

        $(".warning").remove();

        $addToCartForm.addClass("shopify-product-form--unselected-error").find(".cart-warning").html(cartWarning);

        $addToCartBtn.removeAttr("disabled").removeClass("loading");

        $addToCartBtn.find(".icon").removeClass("zoomOut").addClass("zoomIn");

        $addToCartBtn.find("span:not(.icon)").text($addToCartBtn.data("label")).removeClass("zoomOut").addClass("zoomIn");
      } else {
        let urlParams = new URLSearchParams(document.location.search);
        let cartData;

        if (this.country !== "United Kingdom" && this.country !== "United States") {
          cartData = {
            items: [],
          };
        } else {
          cartData = window.PXUTheme.productSectionInstance.getSubscriptionCartData();
        }

        let formData = new FormData($addToCartForm[0]);
        let temp = {};
        let include = true;

        for (let [key, value] of formData.entries()) {
          if (key === "id") {
            let existingItem = cartData.items.find((item) => item.id.toString() === value.toString());
            if (!existingItem) {
              temp["id"] = value;
            } else {
              include = false;
            }
          }

          if (key === "quantity" || key === "selling_plan") {
            if (include) {
              temp[key] = value;
            }
          }
        }

        if (include) {
          cartData.items.push(temp);
        }

        $.ajax({
          url: window.PXUTheme.routes.cart_add_url,
          dataType: "json",
          cache: false,
          method: "post",
          data: cartData,
          beforeSend: function () {
            $addToCartBtn.attr("disabled", "disabled").addClass("loading");
          },
          success: (product) => {
            let $el = $("[data-ajax-cart-trigger]");

            $addToCartBtn.find(".checkmark").addClass("checkmark-active");

            function addedToCart() {
              if (!isScreenSizeLarge()) {
                $el = $("#header [data-ajax-cart-trigger]");
                window.PXUTheme.scrollToTop($el);
              } else {
                $el = $("[data-ajax-cart-trigger]");
              }

              $el.addClass("show-mini-cart");

              $addToCartBtn.find("span").removeClass("fadeInDown");
            }

            window.setTimeout(function () {
              $addToCartBtn.removeAttr("disabled").removeClass("loading");

              $addToCartBtn.find(".checkmark").removeClass("checkmark-active");

              $addToCartBtn.find(".text, .icon").removeClass("zoomOut").addClass("fadeInDown");

              $addToCartBtn.on("webkitAnimationEnd oanimationend msAnimationEnd animationend", addedToCart);
            }, 1000);

            this.updateCart();
            this.showCart();
          },
          error: function (XMLHttpRequest) {
            let response = eval("(" + XMLHttpRequest.responseText + ")");
            response = response.description;

            const cartWarning = `<p class="cart-warning__message animated bounceIn">${response.replace("All 1 ", "All ")}</p>`;

            $(".warning").remove();

            $addToCartForm.find(".cart-warning").html(cartWarning);

            $addToCartBtn.removeAttr("disabled").removeClass("loading");

            $addToCartBtn.find(".icon").removeClass("zoomOut").addClass("zoomIn");

            $addToCartBtn.find("span:not(.icon)").text($addToCartBtn.data("label")).removeClass("zoomOut").addClass("zoomIn");
          },
        });
      }
    }
  },

  showCart() {
    if (this.isCartPage) return; // No drawer on cart page
    const cart = document.getElementById("cart");
    cart.classList.add("active");
    this.html.classList.add("noscroll");
  },

  hideCart() {
    if (this.isCartPage) return; // No drawer on cart page
    const cart = document.getElementById("cart");
    cart.classList.remove("active");
    this.html.classList.remove("noscroll");
  },

  toggleCart(method = false) {
    if (this.isCartPage) return; // No drawer on cart page
    const cart = document.getElementById("cart");
    if (method && method == "show") {
      cart.classList.add("active");
      this.html.classList.add("noscroll");
    } else if (method && method == "hide") {
      cart.classList.remove("active");
      this.html.classList.remove("noscroll");
    } else if (cart.classList.contains("active")) {
      cart.classList.remove("active");
      this.html.classList.remove("noscroll");
    } else {
      cart.classList.add("active");
      this.html.classList.add("noscroll");
    }
  },

  toggleListeners() {
    if (this.isCartPage) return; // No toggle on cart page
    const allToggles = document.querySelectorAll("[data-cart-toggle]");
    for (const toggle of allToggles) {
      toggle.addEventListener("click", () => this.toggleCart());
    }
  },

  cartContainsUvCaseSelection(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return false;
    const uvCaseRegex = /uv\s*case/i;

    return cartData.items.some((item) => {
      const variantOptions = Array.isArray(item.variant_options) ? item.variant_options : [];
      const optionValues = Array.isArray(item.options_with_values)
        ? item.options_with_values.map((opt) => opt && opt.value)
        : [];
      const propertyValues = item.properties ? Object.values(item.properties) : [];
      const candidates = [
        item.product_title,
        item.variant_title,
        ...variantOptions,
        ...optionValues,
        ...propertyValues,
      ];

      return candidates.some((value) => uvCaseRegex.test(String(value || "")));
    });
  },

  cartContainsHeadsSellingPlan(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return false;
    const headsRegex = /\bheads?\b/i;
    const headsHandleRegex = /(^|\W)brush[-_ ]?heads?(\W|$)/i;

    return cartData.items.some((item) => {
      const hasSellingPlan = !!item.selling_plan_allocation;
      if (!hasSellingPlan) return false;

      const handle = String(item.handle || (item.product && item.product.handle) || "");
      if (headsHandleRegex.test(handle)) return true;

      const optionValues = Array.isArray(item.options_with_values)
        ? item.options_with_values.map((opt) => opt && opt.value)
        : [];
      const candidates = [
        item.product_title,
        item.variant_title,
        handle,
        ...optionValues,
      ];

      return candidates.some((value) => headsRegex.test(String(value || "")));
    });
  },

  triggerConvertEvent(eventKey) {
    if (!window.SuriConvertTracking || typeof window.SuriConvertTracking.trigger !== "function") {
      return;
    }

    window.SuriConvertTracking.trigger(eventKey);
  },

  watchCheckoutButton() {
    if (!this.checkoutButton || this.checkoutButton.dataset.convertCheckoutBound === "true") {
      return;
    }

    this.checkoutButton.dataset.convertCheckoutBound = "true";
    this.checkoutButton.addEventListener("click", () => {
      if (
        this.checkoutButton.dataset.convertUvCheckoutEnabled === "true" &&
        this.cartContainsUvCaseSelection(this.cart)
      ) {
        this.triggerConvertEvent("checkout_uv_case");
      }

      if (
        this.checkoutButton.dataset.convertSubscriptionCheckoutEnabled === "true" &&
        this.cartContainsHeadsSellingPlan(this.cart)
      ) {
        this.triggerConvertEvent("checkout_heads_subscription");
      }
    });
  },

  // =============================================================================
  // BUNDLE REVERT BUTTON HANDLING
  // Watch for clicks on revert buttons and trigger bundle revert
  // =============================================================================
  watchBundleRevertButtons(parent = false) {
    let allButtons = [];
    if (parent) {
      allButtons = parent.querySelectorAll("[data-bundle-revert]");
    } else {
      allButtons = document.querySelectorAll("[data-bundle-revert]");
    }

    for (const button of allButtons) {
      // Remove existing listeners by cloning
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      newButton.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const bundleId = newButton.dataset.bundleRevert;
        if (!bundleId) {
          console.error("[Bundle Revert] No bundle ID found on button");
          return;
        }

        // Disable button and show loading state
        newButton.disabled = true;
        newButton.classList.add("loading");
        const originalContent = newButton.innerHTML;
        newButton.innerHTML = '<span>Switching...</span>';

        try {
          // Call the revert method from UpgradeBundle
          if (window.PXUTheme?.upgradeBundle?.revertBundle) {
            const success = await window.PXUTheme.upgradeBundle.revertBundle(bundleId);
            if (!success) {
              console.warn("[Bundle Revert] Revert failed, falling back to remove");
              // Fall back to simple remove if revert fails
              newButton.innerHTML = originalContent;
              newButton.disabled = false;
            }
          } else {
            console.error("[Bundle Revert] UpgradeBundle not available");
            newButton.innerHTML = originalContent;
            newButton.disabled = false;
          }
        } catch (error) {
          console.error("[Bundle Revert] Error during revert:", error);
          newButton.innerHTML = originalContent;
          newButton.disabled = false;
        }
      });
    }
  },

  watchAtcButtons(parent = false) {
    let allButtons = [];
    if (parent) {
      allButtons = parent.querySelectorAll("[data-atc]");
    } else {
      allButtons = document.querySelectorAll("[data-atc]");
    }

    for (const button of allButtons) {
      button.addEventListener("click", (event) => {
        event.preventDefault();

        this.cartEl.classList.add("disabled");

        const target = event.currentTarget;
        const variantId = parseInt(target.dataset.atc, 10); // Parse variant ID
        const quantity = target.dataset.atcQty !== undefined ? parseInt(target.dataset.atcQty, 10) : 1; // Default to 1 if not set

        if (isNaN(variantId)) {
          console.error("Invalid or missing variant ID.");
          return;
        }

        if (isNaN(quantity)) {
          console.error("Invalid quantity specified.");
          return;
        }

        if (quantity > 0) {
          // Add items to cart
          const addData = {
            items: [
              {
                id: variantId,
                quantity: quantity,
              },
            ],
          };

          fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "xmlhttprequest",
            },
            body: JSON.stringify(addData),
            credentials: "same-origin",
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then((json) => {
              this.updateCart();
              this.showCart();
            })
            .catch((err) => {
              console.error("Error adding to cart:", err);
            });
        } else if (quantity === 0) {
          // Check if this is a parent item with nested items (BUNDLE REMOVAL)
          const removeNested = target.dataset.removeNested === "true";
          const parentKey = target.dataset.cartItemKey;
          
          if (removeNested) {
            // BUNDLE REMOVAL: Check if items are API-nested or visually-nested
          fetch("/cart.js", {
            method: "GET",
            headers: {
              "X-Requested-With": "xmlhttprequest",
            },
            credentials: "same-origin",
          })
            .then((response) => response.json())
              .then(async (cart) => {
                const parentItem = cart.items.find(item => item.key === parentKey);
                const parentBundleId = parentItem?.properties?.['_bundle_id'];
                const parentLineNumber = cart.items.findIndex(item => item.key === parentKey) + 1;
                
                // Check if children are API-nested (have parent_relationship)
                const hasApiNestedChildren = cart.items.some(item => 
                  item.parent_relationship && item.parent_relationship.parent_key === parentKey
                );
                
                if (hasApiNestedChildren) {
                  // API-NESTED: Just remove the parent, Shopify handles children automatically
                  await fetch("/cart/change.js", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      "X-Requested-With": "xmlhttprequest",
                    },
                    body: JSON.stringify({ line: parentLineNumber, quantity: 0 }),
                    credentials: "same-origin",
                  });
                  
                  // Check if any bundles remain and remove cart attribute if not
                  if (parentBundleId && window.PXUTheme && window.PXUTheme.checkAndRemoveBundleCartAttribute) {
                    await window.PXUTheme.checkAndRemoveBundleCartAttribute(parentBundleId);
                  }
                } else {
                  // VISUALLY-NESTED (discount case): Need to remove each item individually
                  const lineNumbersToRemove = [];
                  
                  cart.items.forEach((item, index) => {
                    const lineNumber = index + 1;
                    
                    // Remove the parent item
                    if (item.key === parentKey) {
                      lineNumbersToRemove.push(lineNumber);
                    }
                    
                    // Remove visually-nested items (same bundle_id but not the parent)
                    if (parentBundleId && 
                        item.properties?.['_bundle_id'] === parentBundleId && 
                        item.properties?.['_bundle_parent'] !== 'true' &&
                        item.key !== parentKey) {
                      lineNumbersToRemove.push(lineNumber);
                    }
                  });
                  
                  // Remove in reverse order
                  lineNumbersToRemove.sort((a, b) => b - a);
                  
                  for (const lineNumber of lineNumbersToRemove) {
                    await fetch("/cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "xmlhttprequest",
                },
                      body: JSON.stringify({ line: lineNumber, quantity: 0 }),
                credentials: "same-origin",
              });
                  }
                  
                  // Check if any bundles remain and remove cart attribute if not
                  if (parentBundleId && window.PXUTheme && window.PXUTheme.checkAndRemoveBundleCartAttribute) {
                    await window.PXUTheme.checkAndRemoveBundleCartAttribute(parentBundleId);
                  }
                }
                
                this.updateCart();
              })
              .catch((err) => {
                console.error("Error removing items from cart:", err);
              });
          } else {
            // REGULAR REMOVAL: Use fast variant_id approach
            const updates = {
              [variantId]: 0,
            };

            fetch("/cart/update.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "xmlhttprequest",
              },
              body: JSON.stringify({ updates }),
              credentials: "same-origin",
            })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then((json) => {
              this.updateCart();
            })
            .catch((err) => {
              console.error("Error removing items from cart:", err);
            });
          }
        } else if (quantity < 0) {
          // Set the quantity to the absolute value (flip to positive)
          const updates = {
            [variantId]: Math.abs(quantity), // Use absolute value
          };

          fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "xmlhttprequest",
            },
            body: JSON.stringify({ updates }),
            credentials: "same-origin",
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then((json) => {
              this.updateCart();
            })
            .catch((err) => {
              console.error("Error adjusting cart:", err);
            });
        }
      });
    }
  },

  // Helper function to extract pack size from options
  extractPackSizeOption(options) {
    if (!options || !Array.isArray(options)) return null;
    return options.find(opt => 
      opt.name && (opt.name.toLowerCase().includes('pack size') || opt.name.toLowerCase().includes('pack'))
    );
  },

  renderItemOptions(options, excludePackSize = false) {
    let html = ``;
    let bundleValue = "";
    let colourValue = "";

    // Get pack size option to exclude if needed
    const packSizeOption = excludePackSize ? this.extractPackSizeOption(options) : null;

    options.forEach((option) => {
      if (option.value != "Default Title") {
        // Skip pack size option if it's being shown in subscription badge
        if (excludePackSize && packSizeOption && option === packSizeOption) {
          return;
        }
        
        if (option.name === "Bundle") {
          bundleValue = option.value;
        } else if (option.name === "Colour" || option.name === "Color") {
          colourValue = option.value;
        } else {
          html += `<p class="property">${option.name}: ${option.value}</p>`;
        }
      }
    });

    // Combine Bundle and Colour if present
    if (bundleValue || colourValue) {
      const combined = [bundleValue, colourValue].filter(Boolean).join(" / ");
      html += `<p class="property">${combined}</p>`;
    }

    return html;
  },

  renderItemProperties(properties, hideBadge = false) {
    if (!properties || typeof properties !== 'object') {
      return '';
    }
    
    let html = ``;
    let bundleBadge = '';
    
    Object.entries(properties).forEach(([key, value]) => {
      if (Array.from(key)[0] != "_") {
        // Check if this is the Bundle Upgrade badge
        if (key === 'Bundle Upgrade' && value === 'Added') {
          // Only show badge if not hidden (for nested items)
          if (!hideBadge) {
            bundleBadge = `<div class="cart__bundle-upgrade-text">
              <span class="cart__bundle-upgrade-label">${key}: ${value}</span>
              <svg class="cart__bundle-upgrade-checkmark" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>`;
          }
        } else {
        html += `<p class="property">${key}: ${value}</p>`;
        }
      }
    });
    
    // Return badge first, then other properties
    return bundleBadge + html;
  },

  formatter(price) {
    return price === 0 ? window.PXUTheme.translation.free_price_text : Shopify.formatMoney(price, this.moneyFormat);
  },

  async renderItemPrice(item) {
    let allProducts = window.allProductUpsells || [];
    let foundProduct = allProducts.find((product) => product.id == item.variant_id);
    
    // Reset original_price to avoid using stale/incorrect values
    let compareAtPrice = null;
    
    // If product not found in allProductUpsells, fetch from Shopify API
    if (!foundProduct) {
      try {
        const response = await fetch(`/products/${item.handle}.js`);
        if (response.ok) {
          const productData = await response.json();
          const variant = productData.variants.find(v => v.id == item.variant_id);
          if (variant && variant.compare_at_price) {
            compareAtPrice = variant.compare_at_price;
          }
        }
      } catch (error) {
        console.warn('Could not fetch product data for variant:', item.variant_id, error);
      }
    } else {
      // Use data from allProductUpsells
      // Note: compare_price in allProductUpsells is already in cents, not dollars
      if (foundProduct.compare_price && foundProduct.compare_price !== "false" && foundProduct.compare_price !== "" && parseFloat(foundProduct.compare_price) > 0) {
        compareAtPrice = parseFloat(foundProduct.compare_price);
      }
    }
    
    // Only set original_price if we have a valid compare-at price
    if (compareAtPrice && compareAtPrice > 0) {
      item.original_price = compareAtPrice;
    } else {
      // If no compare-at price, set original_price to final_price to avoid showing incorrect discounts
      item.original_price = item.final_price;
    }

    let html = '';

    const isEnabled = document.body.getAttribute("data-intelligems-enabled") === "true";

    if (isEnabled) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("Intelligems timeout")), 5000);
        const checkExist = setInterval(() => {
          if (window.igData && Object.keys(window.igData).length > 0) {
            clearTimeout(timeout);
            clearInterval(checkExist);
            resolve();
          }
        }, 100);
      });

      item.ig_final_price = window.igData?.price?.getPriceByVariantId(item.id) * 100;
      item.ig_original_price = window.igData?.price?.getComparePriceByVariantId(item.id) * 100;
    }

    // For subscription items, use line price (total) instead of unit price
    const isSubscription = item.selling_plan_allocation !== undefined;
    const final = item.ig_final_price > 0 
      ? (isSubscription ? item.ig_final_price * item.quantity : item.ig_final_price)
      : (isSubscription ? (item.final_line_price || item.final_price * item.quantity) : item.final_price);
    const original = item.ig_original_price > 0 
      ? (isSubscription ? item.ig_original_price * item.quantity : item.ig_original_price)
      : (isSubscription ? (item.original_line_price || item.original_price * item.quantity) : item.original_price);
    
    // Check if there's a line-level discount applied (discount code)
    const hasLineDiscount = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;
    
    // Calculate the price before discount code (but after compare-at discount)
    // item.price is the original line item price, item.final_price is after discounts
    const priceBeforeDiscount = isSubscription ? (item.original_line_price || item.price * item.quantity) : item.price;

    const savings = Math.round(((original - final) / original) * 100);

    html += `<p class="price"><span>${this.formatter(final)}</span>`;
    
    // If there's a discount code applied
    if (hasLineDiscount && priceBeforeDiscount > final) {
      // If there's also a compare-at price, show only compare-at price (skip middle price)
      if (final !== original && original > priceBeforeDiscount && original > 0 && !isNaN(savings)) {
        html += `&nbsp;<span class="compare-text">${this.formatter(original)}</span>
                <span class="slabel slabel-green">${savings}% off</span></p>`;
      } else {
        // No compare-at price, just show the discount code savings
        const discountSavings = Math.round(((priceBeforeDiscount - final) / priceBeforeDiscount) * 100);
        html += `&nbsp;<span class="compare-text">${this.formatter(priceBeforeDiscount)}</span>
                <span class="slabel slabel-green">${discountSavings}% off</span></p>`;
      }
    } else if (final !== original && final !== 0 && original > 0 && !isNaN(savings)) {
      // No discount code, just compare-at price
      html += `&nbsp;<span class="compare-text">${this.formatter(original)}</span>
              <span class="slabel slabel-green">${savings}% off</span></p>`;
    } else {
      html += `</p>`;
    }

    return html;
  },


  renderDiscounts(item) {
    let discountHTML = '';
    discountHTML += `<div class="discounts">`;
    if(item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
      item.line_level_discount_allocations.forEach((discount) => {
        discountHTML += `<p><span class="gicon gicon--tag"></span> ${discount.discount_application.title} applied</p>`;
      })
    }
    discountHTML += `</div>`;
    return discountHTML;
  },

  renderSellingPlan(alloc, quantity = 1, options = []) {
    if (alloc === undefined) return "";
    const selling_plan_name = alloc.selling_plan.name;
    
    // Try to extract pack size from variant options (e.g., "Pack Size: 4 Pack" -> 4)
    let packSize = null;
    const packSizeOption = this.extractPackSizeOption(options);
    if (packSizeOption && packSizeOption.value) {
      // Extract number from value like "4 Pack" or "4-Pack" or "4"
      const match = packSizeOption.value.match(/(\d+)/);
      if (match) {
        packSize = parseInt(match[1], 10);
      }
    }
    
    // Use pack size from variant if found, otherwise use quantity
    const displayQuantity = packSize || quantity;
    
    // Format badge text: "Pack of 2 - Every 6 Months"
    const badgeText = displayQuantity > 1 ? `Pack of ${displayQuantity} - ${selling_plan_name}` : selling_plan_name;
    return `<div class="cart__item-subscription-badge">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.5 15C18.9558 18.0448 15.7622 21 12 21C7.14776 21 3.58529 17.5101 3 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <path d="M3.5 9C4.89106 5.64934 8.0647 3 12 3C16.7819 3 20.4232 6.48993 21 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <path d="M21 21L21 15.6C21 15.2686 20.7314 15 20.4 15V15L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M9 9L3.6 9V9C3.26863 9 3 8.73137 3 8.4L3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${badgeText}</span>
    </div>`;
  },

  renderTotalSavings() {    
    let totalSavings = 0;

    // Calculate total savings from the highest price to final price
    for (const item of this.cart.items) {
      // Get per-item prices for comparison
      const compareAtPricePerItem = item.original_price || 0;
      const originalPricePerItem = item.original_line_price ? item.original_line_price / item.quantity : 0;
      
      // Use the highest per-item price available
      const highestPricePerItem = Math.max(compareAtPricePerItem, originalPricePerItem);
      
      if (highestPricePerItem > 0 && item.final_price && highestPricePerItem > item.final_price) {
        const currSaving = highestPricePerItem - item.final_price;
        totalSavings += currSaving * item.quantity;
      }
    }

    // Build the cart content
    let cartContent = `<div class="cart__discounts">`;
    cartContent += `<div class="cart__total">
                      <div><strong>Total Savings</strong></div><span><strong>${this.formatter(totalSavings)}</strong></span>
                    </div>`;
    cartContent += `</div>`;

    if(totalSavings == 0) {
      cartContent = ``;
    }

    // Output the final content
    return cartContent;
  },

  renderUpgradeBundleOffer(item, allCartItems, isLastItem = false) {
    // Only show bundle offer if quantity is 1
    if (item.quantity !== 1) {
      return '';
    }

    // =============================================================================
    // BUNDLE DATA RESOLUTION
    // Check for native bundles first, then fall back to custom upgrade bundles
    // =============================================================================
    const variantId = String(item.variant_id);
    const productId = String(item.product_id);
    
    // Check for NATIVE bundles first
    let bundleData =
      (window.nativeBundlesVariants && window.nativeBundlesVariants[variantId]) ||
      (window.nativeBundles && window.nativeBundles[productId]);
    
    // Fall back to CUSTOM upgrade bundles
    if (!bundleData) {
      bundleData =
        (window.upgradeBundlesVariants && window.upgradeBundlesVariants[variantId]) ||
        (window.upgradeBundles && window.upgradeBundles[productId]);
    }
    
    if (!bundleData) {
      return '';
    }

    // Skip if this trigger variant has no matching available bundle variants
    if (Array.isArray(bundleData.unavailable_trigger_variant_ids) &&
        bundleData.unavailable_trigger_variant_ids.map(String).includes(variantId)) {
      return '';
    }

    // Check target markets - if specified, only show bundle to those markets
    const currentMarket = (window.currentMarketHandle || '').toLowerCase();
    if (bundleData.target_markets && bundleData.target_markets.length > 0) {
      const targetMarkets = bundleData.target_markets.map(m => m.toLowerCase());
      if (!targetMarkets.includes(currentMarket)) {
        return '';
      }
    }

    // Check if bundle is complete (item has bundle_id property)
    const isBundleComplete = item.properties && item.properties['_bundle_id'];
    
    // If bundle is already complete, don't show the offer (it will show "Added" state instead)
    if (isBundleComplete) {
      return '';
    }

    // =============================================================================
    // NATIVE BUNDLE CHECK
    // Show offer for each standalone trigger variant - allow multiple upgrades
    // (e.g. black brush and white brush can each be upgraded to their respective bundles)
    // =============================================================================
    if (bundleData.is_native_bundle) {
      return this.generateBundleOfferHTML(item, bundleData, new Set(), allCartItems, isLastItem);
    }

    // =============================================================================
    // CUSTOM BUNDLE CHECK (existing logic)
    // =============================================================================
    // Check if any bundle products are NOT in cart
    // IMPORTANT: Exclude items that already have a bundle_id (they belong to a different bundle)
    // Items with _bundle_id should NOT count as "in cart" for this bundle offer
    // Items with parent_relationship but no _bundle_id are nested due to discount codes,
    // but should still be considered as "in cart" for bundle purposes
      
    // Get all variant IDs from bundle products
    // Use allowed_variant_ids if available (specific variants in bundle), otherwise use all variants
      const bundleVariantIds = new Set();
      if (bundleData.bundle_products) {
        bundleData.bundle_products.forEach(bundleProduct => {
        // Check if this product has allowed_variant_ids (specific variants in the bundle)
        if (bundleProduct.allowed_variant_ids && bundleProduct.allowed_variant_ids.length > 0) {
          // Only check the allowed variants
          bundleProduct.allowed_variant_ids.forEach(variantId => {
            bundleVariantIds.add(String(variantId));
          });
        } else if (bundleProduct.variants) {
          // No allowed_variant_ids, check all variants
            bundleProduct.variants.forEach(variant => {
              bundleVariantIds.add(String(variant.id));
            });
          }
        });
      }
      
    // Check if we should show the bundle offer
    // Show offer if ANY variant is either:
    // 1. Not in cart at all, OR
    // 2. ALL occurrences in cart belong to other bundles (have _bundle_id)
    // 
    // Hide offer ONLY if at least ONE occurrence of EACH variant is standalone (no _bundle_id)
    
    for (const bundleVariantId of bundleVariantIds) {
      // Find ALL occurrences of this variant in cart (there might be multiple)
      const cartItems = allCartItems.filter(ci => String(ci.variant_id) === bundleVariantId);
        
      if (cartItems.length === 0) {
        // Variant not in cart at all - show offer
        return this.generateBundleOfferHTML(item, bundleData, bundleVariantIds, allCartItems, isLastItem);
      }
      
      // Check if at least ONE occurrence is standalone (no _bundle_id)
      // Items with parent_relationship but no _bundle_id are nested due to discount codes,
      // but should still be considered as "in cart" for bundle purposes
      const hasStandaloneOccurrence = cartItems.some(cartItem => {
        const hasBundleId = cartItem.properties && cartItem.properties['_bundle_id'];
        return !hasBundleId;
      });
      
      if (!hasStandaloneOccurrence) {
        // ALL occurrences belong to other bundles - show offer
        return this.generateBundleOfferHTML(item, bundleData, bundleVariantIds, allCartItems, isLastItem);
          }
      
      // At least one occurrence is standalone - continue checking other variants
    }
    
    // All variants have at least one standalone occurrence in cart - don't show offer
    return '';
  },

  // Helper function to generate bundle offer HTML
  generateBundleOfferHTML(item, bundleData, bundleVariantIds, allCartItems, isLastItem = false) {
    // Use fallback image if not provided
    const imageUrl = bundleData.bundle_image || item.featured_image?.url || item.image || '';
    const displayName = bundleData.bundle_name || 'Bundle Upgrade';
    
    // For native bundles, create bundle data object with pricing for discount calculation
    let discountBundleData = bundleData;
    if (bundleData.is_native_bundle && bundleData.native_bundle) {
      discountBundleData = {
        ...bundleData,
        linked_bundle_price: bundleData.native_bundle.price,
        linked_bundle_compare_price: bundleData.native_bundle.compare_at_price
      };
    }
    
    // Replace [discount] shortcode with actual discount percentage
    const displayDiscount = window.PXUTheme && window.PXUTheme.replaceDiscountShortcode
      ? window.PXUTheme.replaceDiscountShortcode(bundleData.bundle_discount_text || '', discountBundleData)
      : (bundleData.bundle_discount_text || '');
      
    // Determine button text
    // For native bundles: always show "Upgrade Bundle" since it replaces the item
    // For custom bundles: check if any bundle variants are already in cart
    let buttonText = 'Add Bundle';
    
    if (bundleData.is_native_bundle) {
      buttonText = 'Upgrade Bundle';
    } else {
      // Custom bundle logic - check if any bundle variants are already in cart as standalone items
      let hasStandaloneVariants = false;
      for (const bundleVariantId of bundleVariantIds) {
        const cartItems = allCartItems.filter(ci => String(ci.variant_id) === bundleVariantId);
        if (cartItems.length > 0) {
          const hasStandalone = cartItems.some(cartItem => {
            const hasBundleId = cartItem.properties && cartItem.properties['_bundle_id'];
            return !hasBundleId;
          });
          if (hasStandalone) {
            hasStandaloneVariants = true;
            break;
          }
        }
      }
      buttonText = hasStandaloneVariants ? 'Complete Bundle' : 'Add Bundle';
    }

    return `
      <div class="upgrade-bundle-offer-wrapper${isLastItem ? ' upgrade-bundle-offer-wrapper--last' : ''}">
        <div class="upgrade-bundle-offer" data-bundle-offer data-product-id="${item.product_id}">
        <div class="upgrade-bundle-offer__image">
          ${imageUrl ? `<img src="${imageUrl}" alt="${displayName}" />` : ''}
        </div>
        <div class="upgrade-bundle-offer__content">
          <h4 class="upgrade-bundle-offer__title">${displayName}</h4>
          ${displayDiscount ? `<p class="upgrade-bundle-offer__discount">${displayDiscount}</p>` : ''}
        </div>
        <div class="upgrade-bundle-offer__action">
          <button 
            class="upgrade-bundle-offer__button" 
              data-bundle-trigger
            data-product-id="${item.product_id}"
            data-variant-id="${item.variant_id}">
            ${buttonText}
          </button>
          </div>
        </div>
      </div>
    `;
  },

  // =============================================================================
  // INLINE UPGRADE OFFER - Renders upgrade offer attached to cart item
  // =============================================================================
  renderInlineUpgradeOffer(item, allCartItems) {
    // Safety check
    if (!item || !allCartItems) {
      return '';
    }
    
    // Only show bundle offer if quantity is 1
    if (item.quantity !== 1) {
      return '';
    }

    const variantId = String(item.variant_id || '');
    const productId = String(item.product_id || '');
    
    // Check for NATIVE bundles first
    let bundleData =
      (window.nativeBundlesVariants && window.nativeBundlesVariants[variantId]) ||
      (window.nativeBundles && window.nativeBundles[productId]);
    
    // Fall back to CUSTOM upgrade bundles
    if (!bundleData) {
      bundleData =
        (window.upgradeBundlesVariants && window.upgradeBundlesVariants[variantId]) ||
        (window.upgradeBundles && window.upgradeBundles[productId]);
    }
    
    if (!bundleData) {
      return '';
    }

    // Skip if this trigger variant has no matching available bundle variants
    if (Array.isArray(bundleData.unavailable_trigger_variant_ids) &&
        bundleData.unavailable_trigger_variant_ids.map(String).includes(variantId)) {
      return '';
    }

    // Check target markets
    const currentMarket = (window.currentMarketHandle || '').toLowerCase();
    if (bundleData.target_markets && Array.isArray(bundleData.target_markets) && bundleData.target_markets.length > 0) {
      const targetMarkets = bundleData.target_markets.map(m => (m || '').toLowerCase());
      if (!targetMarkets.includes(currentMarket)) {
        return '';
      }
    }

    // Check if bundle is complete
    const isBundleComplete = item.properties && item.properties['_bundle_id'];
    if (isBundleComplete) {
      return '';
    }

    // For custom bundles, check if all variants are already in cart
    if (!bundleData.is_native_bundle) {
      const bundleVariantIds = new Set();
      if (bundleData.bundle_products && Array.isArray(bundleData.bundle_products)) {
        bundleData.bundle_products.forEach(bundleProduct => {
          if (bundleProduct) {
            if (bundleProduct.allowed_variant_ids && Array.isArray(bundleProduct.allowed_variant_ids) && bundleProduct.allowed_variant_ids.length > 0) {
              bundleProduct.allowed_variant_ids.forEach(vid => bundleVariantIds.add(String(vid)));
            } else if (bundleProduct.variants && Array.isArray(bundleProduct.variants)) {
              bundleProduct.variants.forEach(variant => {
                if (variant && variant.id) bundleVariantIds.add(String(variant.id));
              });
            }
          }
        });
      }
      
      // Check if all variants have standalone occurrences
      let allHaveStandalone = true;
      for (const bundleVariantId of bundleVariantIds) {
        const cartItems = allCartItems.filter(ci => ci && String(ci.variant_id) === bundleVariantId);
        if (cartItems.length === 0) {
          allHaveStandalone = false;
          break;
        }
        const hasStandalone = cartItems.some(ci => !(ci.properties && ci.properties['_bundle_id']));
        if (!hasStandalone) {
          allHaveStandalone = false;
          break;
        }
      }
      if (allHaveStandalone) {
        return '';
      }
    }

    // Build component preview tiles (max 4 + overflow indicator)
    // Include: parent product (item being upgraded) + addon (if exists) + all bundle components
    let componentImages = [];
    
    // For native bundles, check if there's a swap_variant for display
    // Also check for addon_product attached to this variant
    // Data comes directly from Liquid output in master_product.variants
    let parentVariantData = null;
    let addonProduct = null;
    let swapVariant = null;
    
    if (bundleData.is_native_bundle && bundleData.master_product?.variants) {
      parentVariantData = bundleData.master_product.variants.find(v => String(v.id) === String(item.variant_id));
      
      if (parentVariantData) {
        addonProduct = parentVariantData.addon_product;
        swapVariant = parentVariantData.swap_variant;
      }
    }
    
    // Add the parent product image first (the item being upgraded)
    // Use swap_variant image if available for display purposes, otherwise cart item image
    if (swapVariant && swapVariant.image) {
      componentImages.push(swapVariant.image);
    } else if (item.image) {
      componentImages.push(item.image);
    }
    
    // Add addon product image if exists (e.g., UV Case)
    if (addonProduct && addonProduct.featured_image) {
      componentImages.push(addonProduct.featured_image);
    }

    if (bundleData.is_native_bundle) {
      // Native bundle - add option_products that are NOT the parent
      // The parent is already shown via item.image above
      const masterProductId = bundleData.master_product?.id;
      
      if (bundleData.option_products && typeof bundleData.option_products === 'object') {
        Object.values(bundleData.option_products).forEach(op => {
          // Skip if this is the parent/master product (already shown)
          if (op && op.id !== masterProductId && op.featured_image) {
            componentImages.push(op.featured_image);
          }
        });
      }
      
      // Also add fixed_variants (products always included regardless of selection)
      if (bundleData.fixed_variants && Array.isArray(bundleData.fixed_variants)) {
        bundleData.fixed_variants.forEach(fv => {
          if (fv) {
            if (fv.image) {
              componentImages.push(fv.image);
            } else if (fv.product && fv.product.featured_image) {
              componentImages.push(fv.product.featured_image);
            }
          }
        });
      }
    } else {
      // Custom bundle - get images from bundle_products
      if (bundleData.bundle_products && Array.isArray(bundleData.bundle_products)) {
        bundleData.bundle_products.forEach(bp => {
          if (bp && bp.featured_image) {
            componentImages.push(bp.featured_image);
          }
        });
      }
    }

    // Build preview HTML (max 4 tiles + overflow)
    const maxVisible = 4;
    const totalComponents = componentImages.length;
    const visibleImages = componentImages.slice(0, maxVisible);
    const overflow = totalComponents > maxVisible ? totalComponents - maxVisible : 0;

    let componentPreviews = '';
    if (visibleImages.length > 0) {
      componentPreviews = `<div class="cart__upgrade-previews">`;
      visibleImages.forEach(img => {
        componentPreviews += `<div class="cart__upgrade-preview"><img src="${img}" alt="Bundle component" /></div>`;
      });
      if (overflow > 0) {
        componentPreviews += `<div class="cart__upgrade-preview cart__upgrade-preview--more">+${overflow}</div>`;
      }
      componentPreviews += `</div>`;
    }

    // Get display info
    const displayName = bundleData.bundle_name || 'Bundle Upgrade';
    const bundleImage = bundleData.bundle_image || '';
    
    // Calculate discount for native bundles
    let discountBundleData = bundleData;
    if (bundleData.is_native_bundle && bundleData.native_bundle) {
      discountBundleData = {
        ...bundleData,
        linked_bundle_price: bundleData.native_bundle.price,
        linked_bundle_compare_price: bundleData.native_bundle.compare_at_price
      };
    }
    
    const displayDiscount = window.PXUTheme && window.PXUTheme.replaceDiscountShortcode
      ? window.PXUTheme.replaceDiscountShortcode(bundleData.bundle_discount_text || '', discountBundleData)
      : (bundleData.bundle_discount_text || '');

    return `
      <div class="cart__upgrade-inline">
        ${bundleImage ? `<div class="cart__upgrade-inline-image"><img src="${bundleImage}" alt="${displayName}" /></div>` : ''}
        <div class="cart__upgrade-inline-body">
          <div class="cart__upgrade-inline-header">
            <span class="cart__upgrade-inline-title">${displayName}</span>
            ${displayDiscount ? `<span class="cart__upgrade-inline-discount">${displayDiscount}</span>` : ''}
          </div>
          ${componentPreviews}
        </div>
        <button 
          class="cart__upgrade-inline-btn" 
          data-bundle-trigger
          data-product-id="${item.product_id}"
          data-variant-id="${item.variant_id}">
          Upgrade
        </button>
      </div>
    `;
  },

  renderUpgradeBundleAdded(item, nestedItemsHtml = '', isLastItem = false, parentItemHtml = '', hasNestedChildren = false, bundleTotalPrice = 0, bundleComparePrice = 0) {
    // =============================================================================
    // BUNDLE DATA RESOLUTION
    // Check for native bundles first, then fall back to custom upgrade bundles
    // =============================================================================
    const variantId = String(item.variant_id);
    const productId = String(item.product_id);
    
    // Check for NATIVE bundles first
    let bundleData =
      (window.nativeBundlesVariants && window.nativeBundlesVariants[variantId]) ||
      (window.nativeBundles && window.nativeBundles[productId]);
    
    // Fall back to CUSTOM upgrade bundles
    if (!bundleData) {
      bundleData =
        (window.upgradeBundlesVariants && window.upgradeBundlesVariants[variantId]) ||
        (window.upgradeBundles && window.upgradeBundles[productId]);
    }
    
    if (!bundleData) {
      return '';
    }

    const imageUrl = bundleData.bundle_image || item.featured_image?.url || item.image || '';
    const displayName = bundleData.bundle_name || 'Bundle Upgrade';
    // Replace [discount] shortcode with actual discount percentage
    const displayDiscount = window.PXUTheme && window.PXUTheme.replaceDiscountShortcode
      ? window.PXUTheme.replaceDiscountShortcode(bundleData.bundle_discount_text || '', bundleData)
      : (bundleData.bundle_discount_text || '');
    
    // Build bundle total price HTML if we have valid prices
    let bundlePriceHtml = '';
    if (bundleTotalPrice > 0) {
      bundlePriceHtml = `<div class="upgrade-bundle-offer__total-price">
        <span class="upgrade-bundle-offer__total-current">${this.formatter(bundleTotalPrice)}</span>
        ${bundleComparePrice > bundleTotalPrice ? `<span class="upgrade-bundle-offer__total-compare">${this.formatter(bundleComparePrice)}</span>` : ''}
      </div>`;
    }

    // =============================================================================
    // BUNDLE REVERT BUTTON LOGIC
    // Check if revert is enabled and if a revert state exists
    // =============================================================================
    const bundleId = item.properties && item.properties['_bundle_id'] ? item.properties['_bundle_id'] : null;
    const bundleRevertEnabled = window.bundleRevertEnabled !== false;
    const hasRevertState = bundleRevertEnabled && bundleId && 
      window.PXUTheme?.upgradeBundle?.getRevertState?.(bundleId) !== null;
    
    // Build the action button - either revert or remove
    let actionButtonHtml = '';
    if (hasRevertState) {
      actionButtonHtml = `
        <button 
          class="upgrade-bundle-offer__button upgrade-bundle-offer__button--revert" 
          data-bundle-revert="${bundleId}"
          title="Switch back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 7C2 7 3.5 4.5 5.5 3C7.5 1.5 10 1 12 2C14 3 15.5 5 15.5 8C15.5 11 13.5 13.5 10.5 14C7.5 14.5 4 13 2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 3V7H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Switch Back</span>
        </button>`;
    } else {
      actionButtonHtml = `
        <button 
          class="upgrade-bundle-offer__button upgrade-bundle-offer__button--remove" 
          data-atc="${item.variant_id}" 
          data-atc-qty="0" 
          data-cart-item-key="${item.key}"
          ${hasNestedChildren ? 'data-remove-nested="true"' : ''}
          title="Remove bundle">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>`;
    }

    return `
      <div class="upgrade-bundle-offer-wrapper${isLastItem ? ' upgrade-bundle-offer-wrapper--last' : ''}">
        <div class="upgrade-bundle-offer upgrade-bundle-offer--added upgrade-bundle-offer--complete" data-bundle-offer data-product-id="${item.product_id}" ${bundleId ? `data-bundle-id="${bundleId}"` : ''} data-upgrade-animation="pending">
          <div class="upgrade-bundle-offer__animation-overlay">
            <div class="upgrade-bundle-offer__success-message">
              <span class="upgrade-bundle-offer__success-text">Success</span>
              <div class="upgrade-bundle-offer__success-badge">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="upgrade-bundle-offer__glitter">
              ${Array.from({length: 12}, (_, i) => `<div class="glitter-particle glitter-particle--${i + 1}"></div>`).join('')}
            </div>
          </div>
          <div class="upgrade-bundle-offer__badge">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1L10.163 5.279L15 6.018L11.5 9.443L12.326 14.259L8 12.019L3.674 14.259L4.5 9.443L1 6.018L5.837 5.279L8 1Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
            <span>Upgraded</span>
          </div>
          <div class="upgrade-bundle-offer__top-row">
            <div class="upgrade-bundle-offer__image">
              ${imageUrl ? `<img src="${imageUrl}" alt="${displayName}" />` : ''}
            </div>
            <div class="upgrade-bundle-offer__content">
              <h4 class="upgrade-bundle-offer__title">${displayName}</h4>
              ${displayDiscount ? `<p class="upgrade-bundle-offer__discount">${displayDiscount}</p>` : ''}
              ${bundlePriceHtml}
            </div>
            <div class="upgrade-bundle-offer__action">
              ${actionButtonHtml}
            </div>
          </div>
          ${parentItemHtml ? `
            <div class="upgrade-bundle-offer__divider"></div>
            <div class="upgrade-bundle-offer__parent-item">${parentItemHtml}</div>
          ` : ''}
          ${nestedItemsHtml ? `
            <div class="upgrade-bundle-offer__nested-items">${nestedItemsHtml}</div>
          ` : ''}
        </div>
      </div>
    `;
  },

  // =============================================================================
  // NATIVE BUNDLE ADDED RENDERING
  // For Shopify native bundles that have been upgraded via the native bundle modal
  // Shows bundle header with components from metaobject data (not from cart items)
  // =============================================================================
  renderNativeBundleAdded(item, nestedItemsHtml = '', isLastItem = false, nativeBundleData = null, bundleTotalPrice = 0, bundleComparePrice = 0) {
    // Use native bundle data if available, otherwise fall back to item data
    const imageUrl = nativeBundleData?.bundle_image || item.featured_image?.url || item.image || '';
    const displayName = nativeBundleData?.bundle_name || item.product_title || 'Bundle';
    
    // Calculate discount text using native bundle prices
    let displayDiscount = '';
    if (nativeBundleData?.bundle_discount_text) {
      // Create bundle data object for discount calculation
      const discountBundleData = {
        ...nativeBundleData,
        linked_bundle_price: bundleTotalPrice,
        linked_bundle_compare_price: bundleComparePrice
      };
      displayDiscount = window.PXUTheme && window.PXUTheme.replaceDiscountShortcode
        ? window.PXUTheme.replaceDiscountShortcode(nativeBundleData.bundle_discount_text, discountBundleData)
        : nativeBundleData.bundle_discount_text;
    }
    
    // Build bundle total price HTML
    let bundlePriceHtml = '';
    if (bundleTotalPrice > 0) {
      bundlePriceHtml = `<div class="upgrade-bundle-offer__total-price">
        <span class="upgrade-bundle-offer__total-current">${this.formatter(bundleTotalPrice)}</span>
        ${bundleComparePrice > bundleTotalPrice ? `<span class="upgrade-bundle-offer__total-compare">${this.formatter(bundleComparePrice)}</span>` : ''}
      </div>`;
    }

    // =============================================================================
    // BUILD COMPONENT HTML FROM METAOBJECT DATA
    // Native bundle components don't exist as cart items - pull from metaobject
    // =============================================================================
    let componentsHtml = '';
    
    if (nativeBundleData) {
      // Find the selected bundle variant to get its option values
      const selectedVariantId = item.variant_id;
      const nativeBundle = nativeBundleData.native_bundle;
      const selectedVariant = nativeBundle?.variants?.find(v => v.id === selectedVariantId);
      
      // Find the parent variant data to get addon_product and swap_variant
      // The original parent variant ID is stored in item properties when the bundle was created
      let parentVariantData = null;
      let addonProduct = null;
      let swapVariant = null;
      
      const originalParentVariantId = item.properties?._original_parent_variant_id;
      const originalParentProductId = item.properties?._original_parent_product_id;
      
      // Look up the correct config using the original parent product ID
      // This handles the case where multiple products use the same native bundle
      let correctBundleData = nativeBundleData;
      
      if (originalParentProductId) {
        // Try product-level config first
        if (window.nativeBundles && window.nativeBundles[originalParentProductId]) {
          correctBundleData = window.nativeBundles[originalParentProductId];
        }
        // Also check variant-level configs
        else if (originalParentVariantId && window.nativeBundlesVariants && window.nativeBundlesVariants[originalParentVariantId]) {
          correctBundleData = window.nativeBundlesVariants[originalParentVariantId];
        }
      }
      
      // Look up the original parent variant using stored ID
      if (originalParentVariantId && correctBundleData.master_product?.variants) {
        parentVariantData = correctBundleData.master_product.variants.find(v => 
          String(v.id) === String(originalParentVariantId)
        );
        
        if (parentVariantData) {
          addonProduct = parentVariantData.addon_product;
          swapVariant = parentVariantData.swap_variant;
        }
      }
      
      const masterProductId = nativeBundleData.master_product?.id;
      let addonProductAdded = false; // Track if addon was added in the loop
      
      if (selectedVariant && nativeBundle?.options) {
        // For each option in the native bundle, find matching variant in option_products
        nativeBundle.options.forEach((option, index) => {
          const optionPosition = index + 1;
          const optionValue = selectedVariant[`option${optionPosition}`];
          const optionProduct = nativeBundleData.option_products?.[optionPosition];
          
          if (optionProduct && optionValue) {
            // Check if this option product is the master/parent product
            const isParentProduct = optionProduct.id === masterProductId;
            
            let componentImage = '';
            
            if (isParentProduct && nativeBundleData.master_product?.variants) {
              // For parent product:
              // 1. Use swap_variant image if available (for display purposes)
              // 2. Otherwise find the matching variant from master_product data
              if (swapVariant && swapVariant.image) {
                componentImage = swapVariant.image;
              } else {
                const masterVariants = nativeBundleData.master_product.variants.filter(v => 
                  v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue
                );
                const masterVariant = masterVariants.find(v => v.image) || masterVariants[0];
                componentImage = masterVariant?.image || nativeBundleData.master_product.featured_image || optionProduct.featured_image || '';
              }
            } else {
              // For other products, find matching variant
              const matchingVariants = optionProduct.variants?.filter(v => 
                v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue || v.title === optionValue
              ) || [];
              const matchingVariant = matchingVariants.find(v => v.image) || matchingVariants[0];
              componentImage = matchingVariant?.image || optionProduct.featured_image || '';
            }
            
            const componentTitle = optionProduct.title || '';
            
            // Build option text (e.g., "Colour: Sea Mist")
            let optionsText = '';
            if (optionValue && optionValue !== 'Default Title') {
              // Try to get the option name from the product
              const optionName = optionProduct.options?.[0]?.name || 'Option';
              optionsText = `<p class="cart-nested-item__option">${optionName}: ${optionValue}</p>`;
            }
            
            componentsHtml += `
              <div class="cart-nested-item cart-nested-item--native${isParentProduct ? ' cart-nested-item--parent' : ''}" data-component-product-id="${optionProduct.id}">
                <div class="cart-nested-item__image">
                  ${componentImage ? `<img src="${componentImage}" alt="${componentTitle}" loading="lazy" />` : ''}
                </div>
                <div class="cart-nested-item__content">
                  <p class="cart-nested-item__title">${componentTitle}</p>
                  ${optionsText}
                </div>
                <div class="cart-nested-item__status">
                  <span class="cart-nested-item__included-label">Included</span>
                  <div class="cart-nested-item__checkmark">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>`;
            
            // After parent product, add addon product if exists (e.g., UV Case)
            if (isParentProduct && addonProduct && addonProduct.title) {
              addonProductAdded = true;
              componentsHtml += `
              <div class="cart-nested-item cart-nested-item--native cart-nested-item--addon" data-component-addon-id="${addonProduct.id}">
                <div class="cart-nested-item__image">
                  ${addonProduct.featured_image ? `<img src="${addonProduct.featured_image}" alt="${addonProduct.title}" loading="lazy" />` : ''}
                </div>
                <div class="cart-nested-item__content">
                  <p class="cart-nested-item__title">${addonProduct.title}</p>
                </div>
                <div class="cart-nested-item__status">
                  <span class="cart-nested-item__included-label">Included</span>
                  <div class="cart-nested-item__checkmark">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>`;
            }
          }
        });
      }
      
      // Add addon product if it exists but wasn't added in the options loop
      // This handles cases where the master product isn't listed as an option_product
      if (!addonProductAdded && addonProduct && addonProduct.title) {
        componentsHtml += `
          <div class="cart-nested-item cart-nested-item--native cart-nested-item--addon" data-component-addon-id="${addonProduct.id}">
            <div class="cart-nested-item__image">
              ${addonProduct.featured_image ? `<img src="${addonProduct.featured_image}" alt="${addonProduct.title}" loading="lazy" />` : ''}
            </div>
            <div class="cart-nested-item__content">
              <p class="cart-nested-item__title">${addonProduct.title}</p>
            </div>
            <div class="cart-nested-item__status">
              <span class="cart-nested-item__included-label">Included</span>
              <div class="cart-nested-item__checkmark">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>`;
      }
      
      // Add fixed variants (always included in bundle)
      if (nativeBundleData.fixed_variants && nativeBundleData.fixed_variants.length > 0) {
        nativeBundleData.fixed_variants.forEach(fixedVariant => {
          const componentImage = fixedVariant.image || '';
          const componentTitle = fixedVariant.product_title || fixedVariant.title || '';
          
          // Build option text if variant has a title different from product title
          let optionsText = '';
          if (fixedVariant.title && fixedVariant.title !== 'Default Title' && fixedVariant.title !== componentTitle) {
            optionsText = `<p class="cart-nested-item__option">${fixedVariant.title}</p>`;
          }
          
          componentsHtml += `
            <div class="cart-nested-item cart-nested-item--native" data-component-variant-id="${fixedVariant.id}">
              <div class="cart-nested-item__image">
                ${componentImage ? `<img src="${componentImage}" alt="${componentTitle}" loading="lazy" />` : ''}
              </div>
              <div class="cart-nested-item__content">
                <p class="cart-nested-item__title">${componentTitle}</p>
                ${optionsText}
              </div>
              <div class="cart-nested-item__status">
                <span class="cart-nested-item__included-label">Included</span>
                <div class="cart-nested-item__checkmark">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>`;
        });
      }
    }
    
    // Use components from metaobject if available, otherwise fall back to nested items
    const finalNestedHtml = componentsHtml || nestedItemsHtml;

    // =============================================================================
    // BUNDLE REVERT BUTTON LOGIC (Native Bundle)
    // For native bundles, the bundle ID is based on the bundle variant ID
    // =============================================================================
    const nativeBundleId = `native_bundle_${item.variant_id}`;
    const bundleRevertEnabled = window.bundleRevertEnabled !== false;
    const hasRevertState = bundleRevertEnabled && 
      window.PXUTheme?.upgradeBundle?.getRevertState?.(nativeBundleId) !== null;
    
    // Build the action button - either revert or remove
    let actionButtonHtml = '';
    if (hasRevertState) {
      actionButtonHtml = `
        <button 
          class="upgrade-bundle-offer__button upgrade-bundle-offer__button--revert" 
          data-bundle-revert="${nativeBundleId}"
          title="Switch back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 7C2 7 3.5 4.5 5.5 3C7.5 1.5 10 1 12 2C14 3 15.5 5 15.5 8C15.5 11 13.5 13.5 10.5 14C7.5 14.5 4 13 2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 3V7H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Switch Back</span>
        </button>`;
    } else {
      actionButtonHtml = `
        <button 
          class="upgrade-bundle-offer__button upgrade-bundle-offer__button--remove" 
          data-atc="${item.variant_id}" 
          data-atc-qty="0" 
          data-cart-item-key="${item.key}"
          data-remove-nested="true"
          title="Remove bundle">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>`;
    }

    return `
      <div class="upgrade-bundle-offer-wrapper${isLastItem ? ' upgrade-bundle-offer-wrapper--last' : ''}">
        <div class="upgrade-bundle-offer upgrade-bundle-offer--added upgrade-bundle-offer--complete upgrade-bundle-offer--native" data-bundle-offer data-product-id="${item.product_id}" data-bundle-id="${nativeBundleId}" data-upgrade-animation="pending">
          <div class="upgrade-bundle-offer__animation-overlay">
            <div class="upgrade-bundle-offer__success-message">
              <span class="upgrade-bundle-offer__success-text">Success</span>
              <div class="upgrade-bundle-offer__success-badge">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="upgrade-bundle-offer__glitter">
              ${Array.from({length: 12}, (_, i) => `<div class="glitter-particle glitter-particle--${i + 1}"></div>`).join('')}
            </div>
          </div>
          <div class="upgrade-bundle-offer__badge">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1L10.163 5.279L15 6.018L11.5 9.443L12.326 14.259L8 12.019L3.674 14.259L4.5 9.443L1 6.018L5.837 5.279L8 1Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
            <span>Upgraded</span>
          </div>
          <div class="upgrade-bundle-offer__top-row">
            <div class="upgrade-bundle-offer__image">
              ${imageUrl ? `<img src="${imageUrl}" alt="${displayName}" />` : ''}
            </div>
            <div class="upgrade-bundle-offer__content">
              <h4 class="upgrade-bundle-offer__title">${displayName}</h4>
              ${displayDiscount ? `<p class="upgrade-bundle-offer__discount">${displayDiscount}</p>` : ''}
              ${bundlePriceHtml}
            </div>
            <div class="upgrade-bundle-offer__action">
              ${actionButtonHtml}
            </div>
          </div>
          ${finalNestedHtml ? `
            <div class="upgrade-bundle-offer__divider"></div>
            <div class="upgrade-bundle-offer__nested-items">${finalNestedHtml}</div>
          ` : ''}
        </div>
      </div>
    `;
  },

  // =============================================================================
  // UPGRADE ANIMATION TRIGGER
  // Triggers success animation when bundle upgrade appears
  // =============================================================================
  triggerUpgradeAnimation(retryCount = 0) {
    // Max 10 retries (2 seconds total)
    if (retryCount > 10) {
      // Mark all pending as completed to prevent future issues
      const pendingBundles = this.cartItemContent.querySelectorAll(
        '.upgrade-bundle-offer[data-upgrade-animation="pending"]'
      );
      pendingBundles.forEach(b => b.setAttribute('data-upgrade-animation', 'completed'));
      return;
    }
    
    // Check if cart is visible (page cart is always visible, drawer needs to be checked)
    if (this.isCartPage) {
      // Cart page is always visible, proceed with animation
    } else {
      const cartDrawer = document.getElementById('cart');
      const cartStyle = cartDrawer ? window.getComputedStyle(cartDrawer) : null;
      const isCartVisible = cartDrawer && 
        cartStyle.display !== 'none' && 
        cartStyle.visibility !== 'hidden' &&
        parseFloat(cartStyle.opacity) > 0;
      
      if (!isCartVisible) {
        setTimeout(() => this.triggerUpgradeAnimation(retryCount + 1), 200);
        return;
      }
    }
    
    // Find all bundles with pending animation
    const pendingBundles = this.cartItemContent.querySelectorAll(
      '.upgrade-bundle-offer[data-upgrade-animation="pending"]'
    );

    pendingBundles.forEach((bundle) => {
      const bundleId = bundle.dataset.bundleId || bundle.dataset.productId;

      // Check if this is a fresh upgrade (within last 15 seconds)
      const storageKey = `bundle_upgrade_time_${bundleId}`;
      const upgradeTimestamp = sessionStorage.getItem(storageKey);
      const timeSinceUpgrade = upgradeTimestamp ? (Date.now() - parseInt(upgradeTimestamp)) : null;
      const isFreshUpgrade = upgradeTimestamp && timeSinceUpgrade < 15000;

      if (!isFreshUpgrade) {
        // Not a fresh upgrade, skip animation
        bundle.setAttribute('data-upgrade-animation', 'completed');
        return;
      }

      // Clear the timestamp so animation only shows once
      sessionStorage.removeItem(storageKey);

      // Mark as playing and trigger animation
      bundle.setAttribute('data-upgrade-animation', 'playing');

      // After animation completes, mark as done
      setTimeout(() => {
        bundle.setAttribute('data-upgrade-animation', 'completed');
      }, 2200);
    });
  },

  updateCart() {
    (async () => {
      try {
        const response = await fetch("/cart.js", {
          method: "GET",
          cache: "no-store",
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const cart = await response.json();
        this.cart = cart;
        let cartContent = "";

        // Check and set Clarity user ID if not already set
        this.updateClarityAttribute();

        // Check and set Bundle Product cart attribute
        this.updateBundleProductAttribute();

        if (cart.attributes["gift-message"]) {
          this.cartNote.querySelector("textarea").value = cart.attributes["gift-message"];
        }

        if (cart.items.length == 0) {
          this.cartFooter.classList.add("hidden");
          this.cartContent.classList.add("hidden");
          this.cartItemCount.classList.add("hidden");
          this.cartEmpty.classList.remove("hidden");
        } else {
          this.cartFooter.classList.remove("hidden");
          this.cartContent.classList.remove("hidden");
          this.cartItemCount.classList.remove("hidden");
          this.cartEmpty.classList.add("hidden");
        }

        this.cartUpsells.innerHTML = "";
        // Reset controls initialization flag when clearing
        if (this.cartUpsells) {
          this.cartUpsells.dataset.controlsInitialized = 'false';
        }

        let discountCodes = [];

        // 🟡 Preload all price HTML blocks asynchronously
        const renderedPrices = await Promise.all(
          cart.items.map((item) => this.renderItemPrice(item))
        );

        let region = document.querySelector("body").dataset.selectedRegion;
        if (region != "GB") {
          switch(region) {
            case "US":
              var urlPart = '/en-us';
              break;
            case "CA":
              var urlPart = '/en-ca';
              break;
            case "BG":
              var urlPart = '/en-de';
              break;
            default:
              var urlPart = '';
          }
        } else {
          var urlPart = '';
        }

        // Group items by parent relationship
        const parentItems = [];
        const nestedItemsMap = new Map(); // Map of parent_key -> array of nested items
        const bundleIdMap = new Map(); // Map of bundle_id -> parent item key (for items without parent_relationship)

        // FIRST: Populate bundleIdMap by finding all bundle parents
        // This must be done BEFORE processing children, regardless of item order
        cart.items.forEach((item, index) => {
          if (item.properties && item.properties['_bundle_id'] && item.properties['_bundle_parent'] === 'true') {
            bundleIdMap.set(item.properties['_bundle_id'], item.key);
          }
        });

        // SECOND: Process parent relationships (API-nested items)
        cart.items.forEach((item, index) => {
          // Check if item has parent_relationship (nested item)
          // IMPORTANT: Don't nest an item under itself (if parent_key matches item's own key)
          if (item.parent_relationship && item.parent_relationship.parent_key) {
            const parentKey = item.parent_relationship.parent_key;
            // Only treat as nested if the parent_key is different from the item's own key
            if (parentKey !== item.key) {
              if (!nestedItemsMap.has(parentKey)) {
                nestedItemsMap.set(parentKey, []);
              }
              nestedItemsMap.get(parentKey).push({ item, index });
            } else {
              // Item has parent_relationship but parent_key is itself - treat as parent item
              parentItems.push({ item, index });
            }
          } else {
            // This is a parent item (show all parent items, even if they have bundle_id)
            parentItems.push({ item, index });
          }
        });
        
        // THIRD: Group items with bundle_id but no parent_relationship
        // These are items that couldn't be nested due to discounts, but should still be displayed as nested
        cart.items.forEach((item, index) => {
          // Skip if already processed as nested
          const alreadyProcessedAsNested = Array.from(nestedItemsMap.values()).some(arr => 
            arr.some(({ item: nestedItem }) => nestedItem.key === item.key)
          );
          
          if (alreadyProcessedAsNested) return;
          
          // If item has bundle_id but NOT marked as parent, and no parent_relationship, find its parent
          if (item.properties && 
              item.properties['_bundle_id'] && 
              item.properties['_bundle_parent'] !== 'true' &&  // Not the parent itself
              !item.parent_relationship) {
            const bundleId = item.properties['_bundle_id'];
            const parentKey = bundleIdMap.get(bundleId);
            
            if (parentKey && parentKey !== item.key) {
              const parentItemIndex = parentItems.findIndex(({ item: parentItem }) => parentItem.key === item.key);
              if (parentItemIndex !== -1) {
                parentItems.splice(parentItemIndex, 1);
              }
              
              if (!nestedItemsMap.has(parentKey)) {
                nestedItemsMap.set(parentKey, []);
              }
              nestedItemsMap.get(parentKey).push({ item, index });
            }
          }
        });

        // Check if discounts section will be shown
        // This matches the logic: discountCodes.length > 0 AND renderTotalSavings() returns content
        let willShowDiscounts = false;
        let hasDiscountCodes = false;
        let totalSavings = 0;
        
        // Check for discount codes (line-level discount allocations)
        for (const item of cart.items) {
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
            hasDiscountCodes = true;
            break;
          }
        }
        
        // If discount codes exist, calculate total savings (matching renderTotalSavings logic)
        if (hasDiscountCodes) {
          for (const item of cart.items) {
            const compareAtPricePerItem = item.original_price || 0;
            const originalPricePerItem = item.original_line_price ? item.original_line_price / item.quantity : 0;
            const highestPricePerItem = Math.max(compareAtPricePerItem, originalPricePerItem);
            
            if (highestPricePerItem > 0 && item.final_price && highestPricePerItem > item.final_price) {
              const currSaving = highestPricePerItem - item.final_price;
              totalSavings += currSaving * item.quantity;
            }
          }
          // Only show discounts if totalSavings > 0 (matching renderTotalSavings condition)
          willShowDiscounts = totalSavings > 0;
        }

        // Render parent items and their nested children
        for (let parentIndex = 0; parentIndex < parentItems.length; parentIndex++) {
          const { item, index } = parentItems[parentIndex];
          const nestedItems = nestedItemsMap.get(item.key) || [];
          // Only treat as last item if it's the last parent AND discounts won't be shown
          const isLastItem = parentIndex === parentItems.length - 1 && !willShowDiscounts;
          
          // Check if item is part of a bundle (has bundle_id property)
          const isBundleUpgraded = item.properties && item.properties['_bundle_id'];
          // Check if this item has nested children (is a bundle parent)
          const hasNestedChildren = nestedItems.length > 0;
          
          // =============================================================================
          // NATIVE BUNDLE UPGRADE DETECTION
          // Native bundles are detected by BOTH properties we add during upgrade:
          // - _bundle_upgrade_native: hidden flag for native bundle detection
          // - Bundle Upgrade: visible flag (same as custom bundles)
          // =============================================================================
          const isNativeBundleUpgrade = item.properties && 
            item.properties['_bundle_upgrade_native'] === 'true' && 
            item.properties['Bundle Upgrade'];
          
          // If bundle is complete (upgraded with nested items), parent goes inside the bundle box
          const isBundleComplete = isBundleUpgraded && hasNestedChildren;
          
          // Native bundle upgrade - ALWAYS render with bundle styling when both properties exist
          // The bundle may have components (has_components) or nested children, but we show
          // the bundle card regardless since we know it was upgraded via our system
          const isNativeBundleComplete = isNativeBundleUpgrade;
          
          // Only render parent item separately if NOT a complete bundle (custom or native)
          if (!isBundleComplete && !isNativeBundleComplete) {
            // Check if this item has an upgrade bundle available
            let upgradeOfferHtml = '';
            try {
              upgradeOfferHtml = this.renderInlineUpgradeOffer(item, cart.items);
            } catch (e) {
              console.error('[Cart] Error rendering inline upgrade offer:', e);
            }
            const hasUpgradeOffer = upgradeOfferHtml !== '';

            // =================================================================
            // SUBSCRIPTION UPSELL STRIP (separate from bundle upgrade system)
            // Type A: swap existing product to subscription
            // Type B: add related subscription product (renders BEFORE bundle)
            // =================================================================
            let subscriptionStripHtml = '';
            let subscriptionStripType = '';
            try {
              const upsellData = this.getSubscriptionUpsellData(item, cart.items);
              if (upsellData) {
                subscriptionStripHtml = this.renderSubscriptionUpsellStrip(item, upsellData);
                subscriptionStripType = 'upsell';
              } else {
                const addonData = this.getSubscriptionAddonData(item, cart.items);
                if (addonData) {
                  subscriptionStripHtml = this.renderSubscriptionAddonStrip(item, addonData, cart.items);
                  subscriptionStripType = 'addon';
                }
              }
            } catch (e) {
              console.error('[Cart] Error rendering subscription strip:', e);
            }
            const hasSubscriptionStrip = subscriptionStripHtml !== '';
            
            cartContent += `<div class="cart__item cart__item--parent cart__item--card${hasUpgradeOffer ? ' cart__item--has-upgrade' : ''}${hasSubscriptionStrip ? ' cart__item--has-subscription' : ''}" data-cart-item-id="${item.variant_id}" data-cart-item-key="${item.key}">
                ${isBundleUpgraded ? '' : `
                <button class="cart__item-remove-x" data-atc="${item.variant_id}" data-atc-qty="0" data-cart-item-key="${item.key}"${hasNestedChildren ? ' data-remove-nested="true"' : ''} title="Remove">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                `}
                <div class="cart__item-image"><a href="${urlPart+item.url}" title="${item.product_title}"><img src="${item.image}" alt="${item.product_title}" /></a></div>
                <div class="cart__item-content">
                  <p class="title"><a href="${urlPart+item.url}" title="${item.product_title}">${item.product_title}</a></p>
                  ${this.renderItemOptions(item.options_with_values, !!item.selling_plan_allocation)}
                  ${this.renderItemProperties(item.properties)}
                  ${this.renderSellingPlan(item.selling_plan_allocation, item.quantity, item.options_with_values)}
                  ${this.renderDiscounts(item)}
                  <div class="cart__item-footer">
                    <div class="cart__item-price">${renderedPrices[index]}</div>
                    ${isBundleUpgraded || item.selling_plan_allocation ? '' : `
                    <div class="cart__item-quantity ${item.price === 0 ? "hidden" : ""}">
                      <div class="minus ${item.product_id == "7765365227708" && item.quantity == 2 ? "disabled" : ""}" data-atc="${item.variant_id}" data-atc-qty="-${item.quantity - 1}"><svg width="16" height="17" viewBox="0 0 16 17" fill="none"><path d="M13.6454 8.85679L13.6454 8.18738H8.65795L7.98854 8.19857V8.18738L3.00109 8.18738L3.00109 8.85679L7.98854 8.85679L8.65795 8.85679L13.6454 8.85679Z" fill="#252525"/></svg></div>
                      <div class="count" data-cart-item-count>${item.quantity}</div>
                      <div class="plus" data-atc="${item.variant_id}" data-atc-qty="-${item.quantity + 1}"><svg width="16" height="17" viewBox="0 0 16 17" fill="none"><path d="M13.3113 8.19078L13.3113 7.52137L8.32385 7.52137L8.32385 2.53392L7.65444 2.53392L7.65444 7.52137L2.66699 7.52137L2.66699 8.19078L7.65444 8.19078L7.65444 13.1782L8.32385 13.1782L8.32385 8.19078L13.3113 8.19078Z" fill="#252525"/></svg></div>
                    </div>
                    `}
                  </div>
                </div>
                ${subscriptionStripType === 'addon' ? subscriptionStripHtml : ''}
                ${upgradeOfferHtml}
                ${subscriptionStripType === 'upsell' ? subscriptionStripHtml : ''}
              </div>`;
          }

          // Render nested bundle items HTML if they exist
          let nestedItemsHtml = '';
          if (nestedItems.length > 0) {
            // Preload prices for nested items (similar to main items)
            const nestedPricesPromises = nestedItems.map(async ({ item: nestedItem }) => {
              // Fetch compare-at price if available
              let compareAtPrice = null;
              const allProducts = window.allProductUpsells || [];
              const foundProduct = allProducts.find((product) => product.id == nestedItem.variant_id);
              
              if (!foundProduct) {
                try {
                  const response = await fetch(`/products/${nestedItem.handle}.js`);
                  if (response.ok) {
                    const productData = await response.json();
                    const variant = productData.variants.find(v => v.id == nestedItem.variant_id);
                    if (variant && variant.compare_at_price) {
                      compareAtPrice = variant.compare_at_price;
                    }
                  }
                } catch (error) {
                  console.warn('Could not fetch product data for nested variant:', nestedItem.variant_id, error);
                }
              } else {
                if (foundProduct.compare_price && foundProduct.compare_price !== "false" && foundProduct.compare_price !== "" && parseFloat(foundProduct.compare_price) > 0) {
                  compareAtPrice = parseFloat(foundProduct.compare_price);
                }
              }
              
              // Set original_price for nested item
              if (compareAtPrice && compareAtPrice > 0) {
                nestedItem.original_price = compareAtPrice;
              } else {
                nestedItem.original_price = nestedItem.final_price;
              }
              
              return nestedItem;
            });
            
            const nestedItemsWithPrices = await Promise.all(nestedPricesPromises);
            
            nestedItemsWithPrices.forEach((nestedItem) => {
              // Get options text (excluding Default Title)
              let optionsText = '';
              if (nestedItem.options_with_values && nestedItem.options_with_values.length > 0) {
                const options = nestedItem.options_with_values
                  .filter(opt => opt.value !== 'Default Title')
                  .map(opt => `${opt.name}: ${opt.value}`)
                  .join(' / ');
                if (options) {
                  optionsText = `<p class="cart-nested-item__option">${options}</p>`;
                }
              }
              
              // Calculate prices and discounts (similar to renderItemPrice logic)
              const final = nestedItem.final_price || 0;
              const original = nestedItem.original_price || nestedItem.final_price || 0;
              const hasLineDiscount = nestedItem.line_level_discount_allocations && nestedItem.line_level_discount_allocations.length > 0;
              const priceBeforeDiscount = nestedItem.price || nestedItem.final_price || 0;
              
              // Build price HTML
              let priceHtml = `<span class="cart-nested-item__price-current">${this.formatter(final)}</span>`;
              let discountBadge = '';
              
              // If there's a discount code applied
              if (hasLineDiscount && priceBeforeDiscount > final) {
                // If there's also a compare-at price, show only compare-at price (skip middle price)
                if (final !== original && original > priceBeforeDiscount && original > 0) {
                  priceHtml += `<span class="cart-nested-item__price-compare">${this.formatter(original)}</span>`;
                } else {
                  // No compare-at price, just show the discount code savings
                  priceHtml += `<span class="cart-nested-item__price-compare">${this.formatter(priceBeforeDiscount)}</span>`;
                }
              } else if (final !== original && final !== 0 && original > 0) {
                // No discount code, just compare-at price
                priceHtml += `<span class="cart-nested-item__price-compare">${this.formatter(original)}</span>`;
              }
              
              nestedItemsHtml += `<div class="cart-nested-item" data-cart-item-id="${nestedItem.variant_id}" data-cart-item-key="${nestedItem.key}">
                  <div class="cart-nested-item__image">
                    <a href="${urlPart+nestedItem.url}" title="${nestedItem.product_title}">
                      <img src="${nestedItem.image}" alt="${nestedItem.product_title}" loading="lazy" />
                    </a>
                  </div>
                  <div class="cart-nested-item__content">
                    <p class="cart-nested-item__title">
                      <a href="${urlPart+nestedItem.url}" title="${nestedItem.product_title}">${nestedItem.product_title}</a>
                    </p>
                    ${optionsText}
                    <div class="cart-nested-item__price-wrapper">
                      ${priceHtml}
                    </div>
                  </div>
                  <div class="cart-nested-item__checkmark">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>`;
            });
          }

          // =============================================================================
          // RENDER BUNDLE UPGRADE (Custom or Native)
          // =============================================================================
          if (isNativeBundleComplete) {
            // NATIVE BUNDLE UPGRADE - render with same styling as custom bundles
            // For native bundles, don't show the parent item separately - 
            // instead render with components in the bundle box styling
            
            // Get bundle data for native bundle (for display name/image/discount text)
            // IMPORTANT: Cart item has the BUNDLE product ID, not the trigger product ID
            // Use nativeBundlesByBundleProductId for reverse lookup
            const nativeBundleProductId = String(item.product_id);
            const nativeBundleData = window.nativeBundlesByBundleProductId && 
              window.nativeBundlesByBundleProductId[nativeBundleProductId];
            
            // Calculate total price from bundle item (native bundles have their own pricing)
            const bundleTotalPrice = item.final_price || item.price || 0;
            const bundleComparePrice = item.original_price || (item.price > item.final_price ? item.price : 0);
            
            // Render native bundle with the "Added" state styling
            cartContent += this.renderNativeBundleAdded(item, nestedItemsHtml, isLastItem, nativeBundleData, bundleTotalPrice, bundleComparePrice);
          } else if (nestedItems.length === 0) {
            // Bundle offer is now rendered inline within the cart item card
            // No separate wrapper needed (empty block is intentional)
          } else {
            // CUSTOM BUNDLE UPGRADE - build parent item HTML to include inside the bundle box
            // Get options text for parent (excluding Default Title)
            let parentOptionsText = '';
            if (item.options_with_values && item.options_with_values.length > 0) {
              const options = item.options_with_values
                .filter(opt => opt.value !== 'Default Title')
                .map(opt => `${opt.name}: ${opt.value}`)
                .join(' / ');
              if (options) {
                parentOptionsText = `<p class="cart-nested-item__option">${options}</p>`;
              }
            }
            
            // Build parent price HTML in same format as nested items
            const parentFinal = item.final_price || 0;
            const parentOriginal = item.original_price || item.final_price || 0;
            const parentHasLineDiscount = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;
            const parentPriceBeforeDiscount = item.price || item.final_price || 0;
            
            let parentPriceHtml = `<span class="cart-nested-item__price-current">${this.formatter(parentFinal)}</span>`;
            let parentDiscountBadge = '';
            
            if (parentHasLineDiscount && parentPriceBeforeDiscount > parentFinal) {
              if (parentFinal !== parentOriginal && parentOriginal > parentPriceBeforeDiscount && parentOriginal > 0) {
                const savingsAmount = parentOriginal - parentFinal;
                parentPriceHtml += `<span class="cart-nested-item__price-compare">${this.formatter(parentOriginal)}</span>`;
                parentDiscountBadge = `<span class="slabel slabel-green cart-nested-item__discount-badge">Save ${this.formatter(savingsAmount)}</span>`;
              } else {
                const savingsAmount = parentPriceBeforeDiscount - parentFinal;
                parentPriceHtml += `<span class="cart-nested-item__price-compare">${this.formatter(parentPriceBeforeDiscount)}</span>`;
                parentDiscountBadge = `<span class="slabel slabel-green cart-nested-item__discount-badge">Save ${this.formatter(savingsAmount)}</span>`;
              }
            } else if (parentFinal !== parentOriginal && parentFinal !== 0 && parentOriginal > 0) {
              // No discount code, just compare-at price
              const savingsAmount = parentOriginal - parentFinal;
              parentPriceHtml += `<span class="cart-nested-item__price-compare">${this.formatter(parentOriginal)}</span>`;
              parentDiscountBadge = `<span class="slabel slabel-green cart-nested-item__discount-badge">Save ${this.formatter(savingsAmount)}</span>`;
            }
            
            // Build parent item HTML (similar to nested items but as the primary component)
            const parentItemHtml = `<div class="cart-nested-item cart-nested-item--parent" data-cart-item-id="${item.variant_id}" data-cart-item-key="${item.key}">
                <div class="cart-nested-item__image">
                  <a href="${urlPart+item.url}" title="${item.product_title}">
                    <img src="${item.image}" alt="${item.product_title}" loading="lazy" />
                  </a>
                </div>
                <div class="cart-nested-item__content">
                  <p class="cart-nested-item__title">
                    <a href="${urlPart+item.url}" title="${item.product_title}">${item.product_title}</a>
                  </p>
                  ${parentOptionsText}
                  <div class="cart-nested-item__price-wrapper">
                    ${parentPriceHtml}
                    ${parentDiscountBadge}
                  </div>
                </div>
                <div class="cart-nested-item__checkmark">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>`;
            
            // Get bundle data for linked prices
            const bundleVariantId = String(item.variant_id);
            const bundleProductId = String(item.product_id);
            const bundleDataForPrices =
              (window.upgradeBundlesVariants && window.upgradeBundlesVariants[bundleVariantId]) ||
              (window.upgradeBundles && window.upgradeBundles[bundleProductId]);
            
            // Calculate bundle total prices - use linked variant prices if available
            let bundleTotalPrice = 0;
            let bundleComparePrice = 0;
            
            if (bundleDataForPrices && bundleDataForPrices.linked_bundle_price) {
              // Use linked variant prices
              bundleTotalPrice = bundleDataForPrices.linked_bundle_price;
              bundleComparePrice = bundleDataForPrices.linked_bundle_compare_price || 0;
            } else {
              // Fall back to calculating from cart items
              bundleTotalPrice = parentFinal;
              bundleComparePrice = parentOriginal > parentFinal ? parentOriginal : parentPriceBeforeDiscount;
              
              // Add nested items prices
              nestedItems.forEach(({ item: nestedItem }) => {
                bundleTotalPrice += nestedItem.final_price || 0;
                const nestedOriginal = nestedItem.original_price || nestedItem.price || nestedItem.final_price || 0;
                bundleComparePrice += nestedOriginal;
              });
            }
            
            // Show "Added" state for bundle button with parent + nested items inside
            cartContent += this.renderUpgradeBundleAdded(item, nestedItemsHtml, isLastItem, parentItemHtml, hasNestedChildren, bundleTotalPrice, bundleComparePrice);
          }

          this.renderLineItemUpsells(item.id);

          if (item.line_level_discount_allocations.length > 0) {
            for (const discount of item.line_level_discount_allocations) {
              discountCodes.push({
                [discount.discount_application.title]: discount.discount_application.total_allocated_amount,
              });
            }
          }
        }

        if (discountCodes.length > 0) {
          cartContent += this.renderTotalSavings();
        }

        // 🔄 Update DOM after prices are fully rendered
        this.cartItemContent.innerHTML = cartContent;
        this.checkoutButton.textContent = `Checkout - ${this.formatter(cart.total_price)}`;
        this.cartItemCount.textContent = `${cart.item_count} items`;
        this.cartIconCount.textContent = cart.item_count > 0 ? cart.item_count : "";
        
        // Update cart page total price display (if it exists)
        const cartTotalPriceEl = document.querySelector('[data-cart-total-price]');
        if (cartTotalPriceEl) {
          cartTotalPriceEl.textContent = this.formatter(cart.total_price);
        }

        // Update all header cart counts (both classic and standard headers)
        document.querySelectorAll('.header-cart').forEach(headerCart => {
          headerCart.classList.toggle('has-cart-count', cart.item_count > 0);
        });
        document.querySelectorAll('[data-bind="itemCount"]').forEach(el => {
          el.textContent = cart.item_count;
        });

        // 🔄 Re-initialize button listeners for cart content (must happen AFTER innerHTML update)
        this.watchAtcButtons(this.cartItemContent);
        this.watchBundleRevertButtons(this.cartItemContent);
        this.watchSubscriptionStrips(); // Subscription upsell strip event bindings

        // 🎉 Trigger upgrade animation for newly upgraded bundles
        // Delay to ensure modal is closed and cart drawer is visible
        setTimeout(() => {
          this.triggerUpgradeAnimation();
        }, 500);

        // Render upsell products (async - uses native recommendations with fallback)
        const upsellWrapper = this.cartUpsells;
        
        // Add updating class for fade effect
        if (upsellWrapper && upsellWrapper.children.length > 0) {
          upsellWrapper.classList.add('updating');
          // Wait for fade out
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const upsellHtml = await this.renderUpsellProducts();
        
        // Clear and update content
        upsellWrapper.innerHTML = upsellHtml;
        
        // Remove updating class to fade in
        upsellWrapper.classList.remove('updating');
        
        // Check if we have products to display (after HTML is set)
        const numberOfUpsells = upsellWrapper.querySelectorAll(".cart__upsells-item").length;
        
        if (numberOfUpsells > 0) {
          this.cartUpsells.closest(".cart__upsells").classList.remove("hidden");
          
          // Small delay to ensure DOM is fully updated before initializing
          setTimeout(() => {
            this.initSwatchListeners();
            this.initUpsellControls();
            this.watchAtcButtons(this.cartEl);
            this.watchBundleRevertButtons(this.cartEl);
          }, 50);
        } else {
          this.cartUpsells.closest(".cart__upsells").classList.add("hidden");
        }
        
        this.calculateFreeShipping();

        // Flickity disabled - using CSS scroll for horizontal layout
        // if ((!this.cartUpsells.flickityInstance && window.innerWidth > 768 && numberOfUpsells > 3) || (window.innerWidth < 768 && numberOfUpsells > 2)) {
        //   this.cartUpsells.flickityInstance = new Flickity(this.cartUpsells, {
        //     wrapAround: true,
        //     dots: false,
        //     prevNextButtons: false,
        //     imagesLoaded: true,
        //     autoPlay: true,
        //     cellAlign: "left",
        //   });
        // }
      } catch (error) {
        console.error("Fetch error:", error);
      }

      this.cartEl.classList.remove("disabled");
      this.calculateFreeShipping();

      if (document.body.getAttribute("data-intelligems-enabled") === "true") {
        window.PXUTheme.jsGlobalProduct.initIntelligems();
      }
    })();
  },


  async fetchRecommendations() {
    // Get unique product IDs from cart items
    const cartProductIds = [...new Set(this.cart.items.map(item => item.product_id))];
    
    if (cartProductIds.length === 0) {
      return [];
    }

    // Fetch recommendations for each product in cart (limit to first 3 products for performance)
    const productIdsToFetch = cartProductIds.slice(0, 3);
    
    // Fetch recommendations from both Search & Discovery sections:
    // - "Complementary products" (intent=complementary) - purely manual
    // - "Related products" (intent=related) - can include manual + automatic fill-in
    
    const fetchForProduct = async (productId) => {
      const results = [];
      
      // Try complementary products first (Search & Discovery "Complementary products" section)
      try {
        const complementaryResponse = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=complementary`);
        if (complementaryResponse.ok) {
          const data = await complementaryResponse.json();
          if (data.products && data.products.length > 0) {
            results.push(...data.products);
          }
        }
      } catch (error) {
        // Silently handle errors
      }
      
      // Also try related products (Search & Discovery "Related products" section)
      // This includes manually configured related products + automatic fill-in
      try {
        const relatedResponse = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=related`);
        if (relatedResponse.ok) {
          const data = await relatedResponse.json();
          if (data.products && data.products.length > 0) {
            results.push(...data.products);
          }
        }
      } catch (error) {
        // Silently handle errors
      }
      
      return results;
    };

    try {
      const allRecommendations = await Promise.all(productIdsToFetch.map(fetchForProduct));
      
      // Flatten and deduplicate recommendations (complementary products come first due to fetch order)
      const seenIds = new Set();
      const uniqueRecommendations = [];
      
      for (const recommendations of allRecommendations) {
        for (const product of recommendations) {
          if (!seenIds.has(product.id)) {
            seenIds.add(product.id);
            uniqueRecommendations.push(product);
          }
        }
      }
      
      return uniqueRecommendations;
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return [];
    }
  },

  getFallbackUpsellProducts() {
    let region = document.querySelector("body").dataset.selectedRegion;
    let upsellProducts = window.upsell_products_uk || [];

    if (region == "US" || region == "CA") {
      upsellProducts = window.upsell_products_us || [];
    }

    return upsellProducts;
  },

  renderColorSwatches(product, selectedVariant) {
    if (!product.variants || product.variants.length <= 1) {
      return '';
    }

    // Find color option index (check for "Color", "Colour", or similar)
    let colorOptionIndex = -1;
    if (product.options) {
      product.options.forEach((option, index) => {
        const optionName = (typeof option === 'string' ? option : option.name || '').toLowerCase();
        if (optionName.includes('color') || optionName.includes('colour')) {
          colorOptionIndex = index;
        }
      });
    }

    if (colorOptionIndex === -1) {
      return '';
    }

    // Get unique color values from available variants
    const seenColors = new Set();
    const colorVariants = [];
    
    product.variants.forEach(variant => {
      if (!variant.available) return;
      
      const colorValue = variant[`option${colorOptionIndex + 1}`] || variant.options?.[colorOptionIndex];
      if (!colorValue || seenColors.has(colorValue)) return;
      
      seenColors.add(colorValue);
      
      // Get variant image
      let variantImage = '';
      if (variant.featured_image) {
        variantImage = typeof variant.featured_image === 'string' 
          ? variant.featured_image 
          : variant.featured_image.src || '';
      } else if (variant.image) {
        variantImage = typeof variant.image === 'string' 
          ? variant.image 
          : variant.image.src || '';
      }
      
      colorVariants.push({
        id: variant.id,
        color: colorValue,
        colorHandle: colorValue.toLowerCase().replace(/\s+/g, '-'),
        image: variantImage,
        price: variant.price,
        isSelected: variant.id === selectedVariant.id
      });
    });

    if (colorVariants.length <= 1) {
      return '';
    }

    // Build swatches HTML - use same CSS classes as product page swatches (e.g., swatch-winter-fern)
    let swatchesHtml = '<div class="cart__upsells-swatches">';
    colorVariants.forEach(cv => {
      // Use the same swatch class pattern as product page (swatch-{color-handle})
      const swatchClass = `swatch-${cv.colorHandle}`;
      
      swatchesHtml += `
        <button 
          class="cart__upsells-swatch ${swatchClass}${cv.isSelected ? ' cart__upsells-swatch--active' : ''}" 
          data-upsell-swatch
          data-variant-id="${cv.id}"
          data-variant-image="${cv.image}"
          data-variant-price="${cv.price}"
          title="${cv.color}"
        ></button>
      `;
    });
    swatchesHtml += '</div>';

    return swatchesHtml;
  },

  initUpsellControls() {
    const scope = this.cartEl || document;
    const wrapper = scope.querySelector('[data-cart-upsells]');
    const prevBtn = scope.querySelector('[data-upsells-prev]');
    const nextBtn = scope.querySelector('[data-upsells-next]');
    
    if (!wrapper || !prevBtn || !nextBtn) return;
    
    // Prevent duplicate initialization
    if (wrapper.dataset.controlsInitialized === 'true') {
      // Just update button states and reset scroll
      wrapper.scrollLeft = 0;
      this.updateUpsellButtonStates();
      return;
    }
    
    // Mark as initialized
    wrapper.dataset.controlsInitialized = 'true';
    
    // Reset scroll position
    wrapper.scrollLeft = 0;
    
    const scrollAmount = 290; // Item width + gap
    
    const updateButtonStates = () => {
      const scrollLeft = wrapper.scrollLeft;
      const scrollWidth = wrapper.scrollWidth;
      const clientWidth = wrapper.clientWidth;
      
      // At the beginning
      prevBtn.disabled = scrollLeft <= 1;
      
      // At the end (with 1px tolerance for rounding)
      nextBtn.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
    };
    
    // Store reference for cleanup
    this.updateUpsellButtonStates = updateButtonStates;
    
    prevBtn.addEventListener('click', () => {
      wrapper.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });
    
    nextBtn.addEventListener('click', () => {
      wrapper.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
    
    // Update on scroll
    wrapper.addEventListener('scroll', updateButtonStates);
    
    // Update on resize (in case viewport changes)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateButtonStates, 100);
    });
    
    // Initial state (with small delay to ensure layout is complete)
    setTimeout(updateButtonStates, 150);
    
    // Update after images load (in case content size changes)
    const images = wrapper.querySelectorAll('img');
    if (images.length > 0) {
      images.forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', updateButtonStates);
        }
      });
    }
  },

  initSwatchListeners() {
    const scope = this.cartEl || document;
    const swatches = scope.querySelectorAll('[data-upsell-swatch]');
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const upsellItem = swatch.closest('.cart__upsells-item');
        if (!upsellItem) return;
        
        const variantId = swatch.dataset.variantId;
        const variantImage = swatch.dataset.variantImage;
        const variantPrice = swatch.dataset.variantPrice;
        
        // Update active swatch
        upsellItem.querySelectorAll('[data-upsell-swatch]').forEach(s => {
          s.classList.remove('cart__upsells-swatch--active');
        });
        swatch.classList.add('cart__upsells-swatch--active');
        
        // Update image
        if (variantImage) {
          const img = upsellItem.querySelector('[data-upsell-image]');
          if (img) img.src = variantImage;
        }
        
        // Update price
        if (variantPrice) {
          const priceEl = upsellItem.querySelector('[data-upsell-price]');
          if (priceEl) {
            priceEl.textContent = Shopify.formatMoney(variantPrice, document.body.getAttribute("data-money-format"));
          }
        }
        
        // Update Add button variant ID
        const addBtn = upsellItem.querySelector('[data-atc]');
        if (addBtn) {
          addBtn.dataset.atc = variantId;
        }
      });
    });
  },

  // Helper to get product title with metafield override
  async getProductDisplayTitle(product) {
    // Check if product already has the metafield (from Liquid data or previous fetch)
    if (product.metafields?.custom?.product_title_override_cart_recommendations) {
      return product.metafields.custom.product_title_override_cart_recommendations;
    }
    
    // Check cached metafields
    if (!this.productTitleCache) {
      this.productTitleCache = {};
    }
    if (this.productTitleCache[product.handle]) {
      return this.productTitleCache[product.handle];
    }
    
    // Try to fetch product page and extract metafield from data attribute or script
    try {
      const response = await fetch(`/products/${product.handle}?view=metafield-title`);
      if (response.ok) {
        const html = await response.text();
        // Look for the metafield value in a data attribute or JSON script
        const match = html.match(/data-cart-title-override="([^"]+)"/);
        if (match && match[1]) {
          this.productTitleCache[product.handle] = match[1];
          return match[1];
        }
      }
    } catch (error) {
      // Silently fail and use default title
    }
    
    // Cache the default title to avoid re-fetching
    this.productTitleCache[product.handle] = product.title;
    return product.title;
  },

  async renderUpsellProducts() {
    let html = ``;
    this.showYMAL = false; // Reset flag
    
    // Get cart variant IDs to filter out products already in cart
    const cartVariantIds = new Set(this.cart.items.map(item => item.id));
    const cartProductIds = new Set(this.cart.items.map(item => item.product_id));
    const isCartEmpty = this.cart.items.length === 0;

    // First, try to get native Shopify recommendations (only if cart has items)
    let recommendations = [];
    if (!isCartEmpty) {
      recommendations = await this.fetchRecommendations();
      // Filter out products already in cart
      recommendations = recommendations.filter(product => !cartProductIds.has(product.id));
    }

    // If we have recommendations, use them
    if (recommendations.length > 0) {
      // Fetch display titles for all products in parallel
      const productsToRender = recommendations.slice(0, 6).filter(product => {
        const firstAvailableVariant = product.variants?.find(v => v.available) || product.variants?.[0];
        return firstAvailableVariant && !cartVariantIds.has(firstAvailableVariant.id);
      });
      
      const displayTitles = await Promise.all(
        productsToRender.map(product => this.getProductDisplayTitle(product))
      );
      
      productsToRender.forEach((product, index) => {
        const firstAvailableVariant = product.variants?.find(v => v.available) || product.variants?.[0];
        const displayTitle = displayTitles[index];

        const price = firstAvailableVariant.price;
        // Handle different image formats - could be string URL or object with src
        let imageUrl = '';
        if (typeof product.featured_image === 'string') {
          imageUrl = product.featured_image;
        } else if (product.featured_image?.src) {
          imageUrl = product.featured_image.src;
        } else if (product.images?.[0]) {
          imageUrl = typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.src;
        }
        
        // Check for color/colour options and build swatches
        const colorSwatchHtml = this.renderColorSwatches(product, firstAvailableVariant);
        
        html += `
          <div class="cart__upsells-item" data-upsell-product-id="${product.id}">
            <a href="/products/${product.handle}" class="cart__upsells-image-link">
              <img src="${imageUrl}" alt="${displayTitle}" data-upsell-image />
            </a>
            <div class="cart__upsells-content">
              <a href="/products/${product.handle}" class="cart__upsells-title-link">
                <div class="cart__upsells-title">${displayTitle}</div>
              </a>
              <div class="cart__upsells-price" data-upsell-price>${Shopify.formatMoney(price, document.body.getAttribute("data-money-format"))}</div>
              ${colorSwatchHtml}
            </div>
            <button class="cart__upsells-add" data-atc="${firstAvailableVariant.id}" aria-label="Add to cart">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        `;
        this.showYMAL = true;
      });
    }
    
    // Fall back to metaobject upsells if no recommendations, cart is empty, or not enough products
    if (isCartEmpty || !this.showYMAL || html === '') {
      const fallbackProducts = this.getFallbackUpsellProducts();
      
      // Filter and prepare fallback products
      const fallbackToRender = fallbackProducts.filter(product => {
        const firstVariant = product.variants?.[0];
        if (!firstVariant || !firstVariant.id) return false;
        return !this.cart.items.find((item) => item.id == firstVariant.id);
      });
      
      // Fetch display titles for fallback products in parallel
      const fallbackDisplayTitles = await Promise.all(
        fallbackToRender.map(product => this.getProductDisplayTitle(product))
      );
      
      fallbackToRender.forEach((product, index) => {
        const firstVariant = product.variants[0];
        const displayTitle = fallbackDisplayTitles[index];
        
        // Handle price - fallback products might have price in cents or as number
        let price = firstVariant.price || product.price;
        if (typeof price === 'string') {
          price = parseFloat(price);
        }
        // If price seems to be in cents (large number), it's already correct for Shopify.formatMoney
        // If it's a small number, it might need conversion, but Shopify.formatMoney handles both
        
        const imageUrl = product.featured_image || '';
        
        // Check for color/colour options and build swatches
        const colorSwatchHtml = this.renderColorSwatches(product, firstVariant);
        
        html += `
          <div class="cart__upsells-item" data-upsell-product-id="${product.id || ''}">
            <a href="/products/${product.handle}" class="cart__upsells-image-link">
              <img src="${imageUrl}" alt="${displayTitle}" data-upsell-image />
            </a>
            <div class="cart__upsells-content">
              <a href="/products/${product.handle}" class="cart__upsells-title-link">
                <div class="cart__upsells-title">${displayTitle}</div>
              </a>
              <div class="cart__upsells-price" data-upsell-price>${Shopify.formatMoney(price, document.body.getAttribute("data-money-format"))}</div>
              ${colorSwatchHtml}
            </div>
            <button class="cart__upsells-add" data-atc="${firstVariant.id}" aria-label="Add to cart">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        `;
        this.showYMAL = true;
      });
    }

    return html;
  },

  renderLineItemUpsells(id) {
    let html = ``;

    // Initialize renderedUpsellProductIds only once
    if (!this.renderedUpsellProductIds) {
      this.renderedUpsellProductIds = [];
    }

    // Find the line item in allProductUpsells with the given ID
    const lineItem = window.allProductUpsells.find((item) => item.id === id);
    if (lineItem && lineItem.upsell_product) {
      const upsellProduct = window.allProductUpsells.find((item) => item.id === lineItem.upsell_product);
      if (upsellProduct) {
          if (this.renderedUpsellProductIds.includes(upsellProduct.id)) {
            return false; // Skip if the product ID is already rendered
          }
          // Add the product ID to the array and render only once
          this.renderedUpsellProductIds.push(upsellProduct.id);

          html += `
            <div class="cart__upsells-item">
              <a href="${upsellProduct.url}">
                <img src="${upsellProduct.image}" alt="Upsell for ${upsellProduct.id}" />
                <div class="cart__upsells-title">${upsellProduct.title}</div>
                <div class="cart__upsells-price">${Shopify.formatMoney((upsellProduct.price / 100).toFixed(2), document.body.getAttribute("data-money-format"))}</div>
              </a>
              <button type="button" class="btn btn--outline btn--block btn--smaller" data-atc="${upsellProduct.id}">Add</button>
            </div>
          `;
          this.showYMAL = true;
      }
    }

    this.cartUpsells.innerHTML += html;
  },

  renderShippingDays(minDays, maxDays, includeWeekends) {
    const today = new Date();

    // Helper function to add days while considering weekends
    function addDays(date, days, includeWeekends) {
      let result = new Date(date);
      while (days > 0) {
        result.setDate(result.getDate() + 1);
        if (includeWeekends || (result.getDay() !== 0 && result.getDay() !== 6)) {
          days--;
        }
      }
      return result;
    }

    // Format the date as "Day, Month Day"
    function formatDate(date) {
      const options = { weekday: "short", month: "short", day: "numeric" };
      return date.toLocaleDateString("en-US", options);
    }

    const minDeliveryDate = addDays(today, minDays, includeWeekends);
    const maxDeliveryDate = addDays(today, maxDays, includeWeekends);

    const formattedMinDate = formatDate(minDeliveryDate);
    const formattedMaxDate = formatDate(maxDeliveryDate);

    return `<p>Est. delivery <strong>${formattedMinDate}</strong> to <strong>${formattedMaxDate}</strong></p>`;
  },

  addFooterText() {
    // Get blocks that should be shown for current country
    const blocks = this.footer_text_blocks || [];
    const items = [];

    blocks.forEach(block => {
      if (!block.enabled) return; // Skip disabled blocks

      // Check if block should show for current country
      const shouldShow = 
        block.countries === "all" ||
        (block.countries === "uk" && this.country === "United Kingdom") ||
        (block.countries === "us" && this.country === "United States") ||
        (block.countries === "other" && this.country !== "United Kingdom" && this.country !== "United States");

      if (!shouldShow) return;

      let text = block.text || "";

      // Replace {free_shipping_amount} shortcode
      if (this.country === "United Kingdom" && this.free_shipping_amount_uk) {
        text = text.replace("{free_shipping_amount}", this.formatter(this.free_shipping_amount_uk));
      } else if (this.country === "United States" && this.free_shipping_amount_us) {
        text = text.replace("{free_shipping_amount}", this.formatter(this.free_shipping_amount_us));
      } else {
        text = text.replace("{free_shipping_amount}", "");
      }

      // Replace {next_day_delivery} shortcode
      if (this.next_day_delivery_text && this.next_day_delivery_price) {
        const priceFormatted = this.formatter(this.next_day_delivery_price);
        const nextDayText = this.next_day_delivery_text.replace("{price}", priceFormatted);
        text = text.replace("{next_day_delivery}", nextDayText);
      } else {
        text = text.replace("{next_day_delivery}", "");
      }

      // Only add if text is not empty after replacements
      text = text.trim();
      if (text.length > 0) {
        items.push({ 
          text: text,
          icon: block.icon || '',
          background_color: block.background_color || ''
        });
      }
    });

    // If no items, skip rendering
    if (items.length === 0) return;

    const layout = this.footer_text_layout || 'marquee';
    
    if (layout === 'grid') {
      // Grid layout
      const gridHtml = `<div class="cart__footer-text cart__footer-text--grid">
        ${items.map(item => {
          const style = item.background_color ? `style="background-color: ${item.background_color};"` : '';
          const icon = item.icon && item.icon !== 'none' ? `<div class="gicon gicon--${item.icon}"></div>` : '';
          return `<div class="cart__footer-text__grid-item" ${style}>
            ${icon}
            <div class="cart__footer-text__grid-text">${item.text}</div>
          </div>`;
        }).join('')}
      </div>`;
      this.cartFooter.insertAdjacentHTML("beforeend", gridHtml);
    } else if (layout === 'icon-stack') {
      const iconStackHtml = `<div class="cart__footer-text cart__footer-text--icon-stack">
        ${items.map(item => {
          const icon = item.icon && item.icon !== 'none'
            ? `<div class="gicon gicon--${item.icon}"></div>` : '';
          return `<div class="cart__footer-text__icon-stack-item">
            ${icon}
            <div class="cart__footer-text__icon-stack-text">${item.text}</div>
          </div>`;
        }).join('')}
      </div>`;
      this.cartFooter.insertAdjacentHTML("beforeend", iconStackHtml);
    } else {
      // Marquee layout (default)
      const bullet = '<span class="cart__footer-text__bullet">•</span>';
      const content = items.map(item => item.text).join(bullet) + bullet;
      const marqueeHtml = `<div class="cart__footer-text cart__footer-text--marquee">
        <div class="cart__footer-text__track">
          <span class="cart__footer-text__content">${content}</span>
          <span class="cart__footer-text__content" aria-hidden="true">${content}</span>
        </div>
      </div>`;
      this.cartFooter.insertAdjacentHTML("beforeend", marqueeHtml);
    }

    // Append payment icons block if enabled
    const paymentIconsTemplate = document.getElementById("cart-payment-icons-template");
    if (paymentIconsTemplate && this.enable_payment_icons !== false) {
      this.cartFooter.appendChild(paymentIconsTemplate.content.cloneNode(true));
    }
  },

  getClarityVisitorUserId() {
    // Get Clarity visitor user ID from sessionStorage or window
    return sessionStorage.getItem('clarityVisitorUserId') || window._clarityVisitorUserId || null;
  },

  updateClarityAttribute(forceUpdate = false) {
    const clarityUserId = this.getClarityVisitorUserId();
    
    // Check if already set in cart attributes
    if (!forceUpdate && this.cart && this.cart.attributes && this.cart.attributes.clarityUserId) {
      return;
    }
    
    if (clarityUserId) {
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: {
            "clarityUserId": clarityUserId,
          },
        }),
      })
        .then((response) => {
          if (!response.ok) {
            console.error("Failed to update Clarity user ID");
          }
        })
        .catch((error) => {
          console.error("Error updating Clarity user ID:", error);
        });
    }
  },

  /**
   * Bundle Product Detection
   * Checks if any cart items are from the bundles collection and sets cart attribute
   */
  updateBundleProductAttribute() {
    // Check if bundle detection is enabled
    if (!window.PXUTheme.bundleDetection || !window.PXUTheme.bundleDetection.enabled) {
      return;
    }

    const bundleProductIds = window.PXUTheme.bundleDetection.productIds || [];
    if (bundleProductIds.length === 0) {
      return;
    }

    // Check if any cart item is in the bundle collection
    let hasBundleProduct = false;
    if (this.cart && this.cart.items) {
      hasBundleProduct = this.cart.items.some(item => 
        bundleProductIds.includes(item.product_id)
      );
    }

    // Get current attribute value to avoid unnecessary updates
    const currentValue = this.cart && this.cart.attributes && this.cart.attributes["Bundle Product"];
    
    // Only make API call if we need to set it to "true", or if it's currently "true" and needs to be removed
    // Don't make a call just to set "false" when attribute doesn't exist
    if (hasBundleProduct && currentValue !== "true") {
      // Need to set attribute to "true"
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: {
            "Bundle Product": "true",
          },
        }),
      })
        .catch(() => {});
    } else if (!hasBundleProduct && currentValue === "true") {
      // Need to remove/clear the attribute
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: {
            "Bundle Product": "",
          },
        }),
      })
        .catch(() => {});
    }
    // If attribute is not set and no bundle product, do nothing (avoid unnecessary API call)
  },

  watchCartNote() {
    // On the page cart, set the textarea placeholder dynamically from section data
    if (this.isCartPage && this.gift_note_text) {
      const textarea = this.cartNote.querySelector("textarea");
      if (textarea) {
        textarea.placeholder = this.gift_note_text;
      }
    }

    updateCartGiftMessage = (message) => {
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: {
            "gift-message": message,
            "gift message": message,
          },
        }),
      })
        .then((response) => {
          if (!response.ok) {
            console.error("Failed to update the gift message");
          }
        })
        .catch((error) => {
          console.error("Error updating the gift message:", error);
        });
    };

    this.cartNote.querySelector("textarea").addEventListener("input", () => {
      clearTimeout(this.cartNoteUpdateTimeout);
      this.cartNoteUpdateTimeout = setTimeout(() => {
        const giftMessage = this.cartNote.querySelector("textarea").value.trim();
        if (giftMessage) {
          updateCartGiftMessage(giftMessage);
        }
      }, 2000);
    });

    this.cartNote.querySelector(".cart__note-title").addEventListener("click", (event) => {
      if (this.cartNote.classList.contains("active")) {
        this.cartNote.classList.remove("active");
      } else {
        this.cartNote.classList.add("active");
      }
    });
  },

  // =============================================================================
  // SUBSCRIPTION UPSELL SYSTEM
  // =============================================================================
  //
  // Self-contained subscription upsell feature for the cart drawer.
  // Renders compact strips below cart items to encourage subscription sign-ups.
  //
  // Two strip types:
  //   Type A ("Swap") - Product is in cart without a selling plan; offer to swap
  //                     it for the subscription counterpart.
  //   Type B ("Addon") - A related subscription product is missing from cart;
  //                      offer to add it (e.g. brush in cart -> add heads sub).
  //
  // Data sources (populated by Liquid in sections/cart.liquid):
  //   window.subscriptionUpsells        – keyed by product ID (Type A)
  //   window.subscriptionAddonUpsells   – keyed by product ID (Type B)
  //
  // Public API consumed by the cart render loop:
  //   getSubscriptionUpsellData(item, allCartItems)  -> upsell data or null
  //   getSubscriptionAddonData(item, allCartItems)   -> addon data or null
  //   renderSubscriptionUpsellStrip(item, data)       -> HTML string
  //   renderSubscriptionAddonStrip(item, data, items) -> HTML string
  //   handleSubscriptionSwap(item, plan, variantId)   -> async cart mutation
  //   handleSubscriptionAddonAdd(data, plan, varId)   -> async cart mutation
  //   watchSubscriptionStrips()                        -> event bindings
  // =============================================================================

  // ---------------------------------------------------------------------------
  // Detection: determine if an item qualifies for a subscription strip
  // ---------------------------------------------------------------------------

  /**
   * Type A detection – check if a cart item can be swapped to a subscription.
   * Returns the upsell data object or null.
   */
  getSubscriptionUpsellData(item, allCartItems) {
    if (!item || !allCartItems) return null;

    // Already on a selling plan – nothing to upsell
    if (item.selling_plan_allocation) return null;

    const productId = String(item.product_id || '');
    const data = window.subscriptionUpsells && window.subscriptionUpsells[productId];
    if (!data || !data.plans || data.plans.length === 0) return null;

    // Market gating
    if (!this._subscriptionMarketCheck(data.target_markets)) return null;

    // Per-variant check: only suppress if a subscription item was swapped
    // FROM this specific variant (tracked via _upsell_source_variant property)
    const itemVariantId = String(item.variant_id || '');
    const alreadySwapped = allCartItems.some(ci =>
      ci.selling_plan_allocation &&
      ci.properties &&
      String(ci.properties._upsell_source_variant) === itemVariantId
    );
    if (alreadySwapped) return null;

    return data;
  },

  /**
   * Type B detection – check if a cart item should show an addon subscription strip.
   * Returns the addon data object or null.
   */
  getSubscriptionAddonData(item, allCartItems) {
    if (!item || !allCartItems) return null;

    // Already on a selling plan – the item is a subscription itself, so we
    // never show an upsell/addon strip on top of it (mirrors Type A).
    if (item.selling_plan_allocation) return null;

    const productId = String(item.product_id || '');
    const data = window.subscriptionAddonUpsells && window.subscriptionAddonUpsells[productId];
    if (!data || !data.plans || data.plans.length === 0) return null;

    // Market gating
    if (!this._subscriptionMarketCheck(data.target_markets)) return null;

    // --- Per-variant addon linking ---
    // When an addon subscription is added, the addon cart item receives a hidden
    // property `_addon_parent_variant` = the parent variant ID it was added for.
    // This lets us show/hide the addon strip per-variant (e.g. two brushes in
    // different colours can each independently subscribe to heads).
    const itemVariantId = String(item.variant_id || '');

    // Resolve offer scope up-front so both the linked-addon check and the
    // cross-product check can use it.
    const addonProductId = data.addon_product_id ? String(data.addon_product_id) : null;
    const addonSubProductIds = new Set(
      data.plans.map(p => String(p.subscription_product_id)).filter(Boolean)
    );
    const offerSellingPlanIds = new Set(
      data.plans.map(p => String(p.selling_plan_id)).filter(Boolean)
    );

    // Per-variant check: only suppress if a subscription addon is already
    // linked to THIS specific variant via _addon_parent_variant AND that
    // linked addon is for the SAME product family this offer would add.
    // Without the product-family check, an unrelated addon (e.g. heads)
    // linked to a parent variant would suppress every other offer on that
    // parent (e.g. a separate toothpaste subscription on a Welcome Set).
    const linkedAddonInCart = allCartItems.some(ci => {
      if (!ci.selling_plan_allocation) return false;
      if (!ci.properties) return false;
      if (String(ci.properties._addon_parent_variant) !== itemVariantId) return false;
      const ciProductId = String(ci.product_id);
      if (addonProductId && ciProductId === addonProductId) return true;
      if (addonSubProductIds.has(ciProductId)) return true;
      return false;
    });
    if (linkedAddonInCart) return null;

    // Cross-product suppression: hide this addon strip when a related cart
    // item already represents what this offer would add. Two cases qualify:
    //   1. The related item is on a selling plan that matches one of this
    //      offer's plans (true duplicate already in the cart — e.g. bundle
    //      offers a toothpaste sub on plan X, toothpaste already in cart on
    //      plan X).
    //   2. The related item is in the cart WITHOUT a selling plan and is the
    //      addon's own subscription product, so it would show its own Type A
    //      swap strip — keep the dedupe in that case (e.g. Sea Mist brush +
    //      Sea Mist heads → heads shows Type A, brush hides addon).
    // Items on an unrelated selling plan should NOT suppress the offer.
    // The colour gate only applies when BOTH the parent item and the
    // related cart item have a colour to compare. Uncoloured products like
    // toothpaste fall back to selling-plan / product-family overlap alone.
    let colourOption = (data.colour_match_option || '').toLowerCase();
    if (!colourOption && item.options_with_values) {
      const autoDetected = item.options_with_values.find(o => /^colou?r$/i.test(o.name));
      if (autoDetected) colourOption = autoDetected.name.toLowerCase();
    }
    const itemColour = colourOption && item.options_with_values
      ? (item.options_with_values.find(o => o.name.toLowerCase() === colourOption) || {}).value
      : null;

    const matchingRelatedItem = allCartItems.some(ci => {
      if (String(ci.product_id) === productId) return false;
      const ciProductId = String(ci.product_id);
      const isAddonProduct = addonProductId && ciProductId === addonProductId;
      const isSubscriptionProduct = addonSubProductIds.has(ciProductId);
      if (!isAddonProduct && !isSubscriptionProduct) {
        const ciUpsellData = window.subscriptionUpsells && window.subscriptionUpsells[ciProductId];
        if (!ciUpsellData || !ciUpsellData.plans || ciUpsellData.plans.length === 0) return false;
        const ciSubProductIds = new Set(
          ciUpsellData.plans.map(p => String(p.subscription_product_id)).filter(Boolean)
        );
        if (![...addonSubProductIds].some(id => ciSubProductIds.has(id))) return false;
      }

      // Selling-plan gate: if the related item is already on a selling plan,
      // only treat it as a duplicate when that plan is one of the plans
      // this offer would add. If it's on an unrelated plan, the offer still
      // applies and should render. If it has no plan at all, fall through
      // so the colour-match dedupe below can still apply for products that
      // have a colour to compare.
      const ciSellingPlanId = ci.selling_plan_allocation
        && ci.selling_plan_allocation.selling_plan
        && ci.selling_plan_allocation.selling_plan.id;
      if (ciSellingPlanId) {
        if (offerSellingPlanIds.size === 0) return false;
        if (!offerSellingPlanIds.has(String(ciSellingPlanId))) return false;
      }

      // Colour gate (only when both sides actually have a colour). Skipping
      // the colour check for uncoloured products lets the bundle/toothpaste
      // case dedupe purely on selling plan + subscription product overlap.
      if (colourOption && itemColour) {
        let ciColour = ci.options_with_values
          ? (ci.options_with_values.find(o => o.name.toLowerCase() === colourOption) || {}).value
          : null;
        if (!ciColour && ci.properties && ci.properties._upsell_source_color) {
          ciColour = ci.properties._upsell_source_color;
        }
        if (ciColour && ciColour.toLowerCase() !== itemColour.toLowerCase()) return false;
      }

      return true;
    });
    if (matchingRelatedItem) return null;

    return data;
  },

  /**
   * Shared market check for subscription strips.
   * Returns true if the strip should show for the current market.
   */
  _subscriptionMarketCheck(targetMarkets) {
    if (!targetMarkets || !Array.isArray(targetMarkets) || targetMarkets.length === 0) {
      return true; // no restriction = show everywhere
    }
    const currentMarket = (window.currentMarketHandle || '').toLowerCase();
    return targetMarkets.some(m => (m || '').toLowerCase() === currentMarket);
  },

  // ---------------------------------------------------------------------------
  // Rendering: build HTML for subscription strips
  // ---------------------------------------------------------------------------

  /**
   * Subscription icon SVG (recycle arrows) – reused across both strip types.
   */
  _subscriptionIcon() {
    return `<svg class="cart__sub-strip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.5 15C18.9558 18.0448 15.7622 21 12 21C7.14776 21 3.58529 17.5101 3 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <path d="M3.5 9C4.89106 5.64934 8.0647 3 12 3C16.7819 3 20.4232 6.48993 21 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <path d="M21 21L21 15.6C21 15.2686 20.7314 15 20.4 15V15L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M9 9L3.6 9V9C3.26863 9 3 8.73137 3 8.4L3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  },

  /**
   * Convert Shopify rich_text JSON to plain HTML.
   * Rich text fields return a JSON AST with type/children/value nodes.
   */
  _parseRichText(richText) {
    if (!richText) return '';
    // If it's already a plain string, return as-is
    if (typeof richText === 'string') return richText;
    // If it's a rich_text JSON object, parse the tree
    if (typeof richText === 'object' && richText.children) {
      return richText.children.map(node => this._renderRichTextNode(node)).join('');
    }
    return '';
  },

  /**
   * Recursively render a single rich_text AST node to HTML.
   */
  _renderRichTextNode(node) {
    if (!node) return '';
    if (node.type === 'text') {
      let text = node.value || '';
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      return text;
    }
    const children = (node.children || []).map(c => this._renderRichTextNode(c)).join('');
    switch (node.type) {
      case 'paragraph': return `<p>${children}</p>`;
      case 'heading': return `<h${node.level || 3}>${children}</h${node.level || 3}>`;
      case 'list': return node.listType === 'ordered' ? `<ol>${children}</ol>` : `<ul>${children}</ul>`;
      case 'list-item': return `<li>${children}</li>`;
      case 'link': return `<a href="${node.url || '#'}" target="${node.target || '_self'}">${children}</a>`;
      case 'root': return children;
      default: return children;
    }
  },

  /**
   * Render plan selector for the expanded state.
   * Single plan  -> inline info row (thumb, title, recurrence — not a button).
   * Multi plan   -> custom dropdown with thumb inside trigger.
   * Badge is extracted and returned separately for positioning on the collapsed bar.
   */
  _renderPlanSelector(plans, thumbSrc) {
    if (!plans || plans.length === 0) return '';

    const thumbImg = thumbSrc ? `<img class="cart__sub-strip-selector-thumb" src="${thumbSrc}" alt="" />` : '';

    // Single plan: show as inline info (not clickable)
    if (plans.length === 1) {
      const plan = plans[0];
      return `<div class="cart__sub-strip-plan-info"
                   data-subscription-plan-select
                   data-plan-index="0"
                   data-selling-plan-id="${plan.selling_plan_id || ''}"
                   data-subscription-product-id="${plan.subscription_product_id || ''}"
                   data-subscription-variant-id="${plan.subscription_variant_id || ''}"
                   data-plan-quantity="${plan.quantity || 1}">
        ${thumbImg}
        <div class="cart__sub-strip-plan-text">
          <span class="cart__sub-strip-plan-title">${plan.displayed_text || ''}</span>
          <span class="cart__sub-strip-plan-meta">
            ${plan.price_text ? `<span class="cart__sub-strip-plan-recurrence">${plan.price_text}</span>` : ''}
            ${plan.badge_text ? `<span class="cart__sub-strip-plan-badge-inline">${plan.badge_text}</span>` : ''}
          </span>
        </div>
      </div>`;
    }

    // Multiple plans: custom dropdown
    const firstPlan = plans[0];
    const chevronSvg = `<svg class="cart__sub-strip-dropdown-arrow" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    return `<div class="cart__sub-strip-dropdown-wrapper" data-subscription-dropdown>
      <button class="cart__sub-strip-dropdown-trigger" data-subscription-dropdown-toggle>
        ${thumbImg}
        <span class="cart__sub-strip-dropdown-label">
          <span>${firstPlan.displayed_text || ''}</span>
          <span class="cart__sub-strip-plan-meta">
            ${firstPlan.price_text ? `<span class="cart__sub-strip-plan-recurrence">${firstPlan.price_text}</span>` : ''}
            ${firstPlan.badge_text ? `<span class="cart__sub-strip-plan-badge-inline">${firstPlan.badge_text}</span>` : ''}
          </span>
        </span>
        ${chevronSvg}
      </button>
      <div class="cart__sub-strip-dropdown-list" data-subscription-dropdown-list>
        ${plans.map((plan, i) => `
          <button class="cart__sub-strip-dropdown-option${i === 0 ? ' is-selected' : ''}"
                  data-subscription-plan-select
                  data-plan-index="${i}"
                  data-selling-plan-id="${plan.selling_plan_id || ''}"
                  data-subscription-product-id="${plan.subscription_product_id || ''}"
                  data-subscription-variant-id="${plan.subscription_variant_id || ''}"
                  data-plan-quantity="${plan.quantity || 1}">
            <span class="cart__sub-strip-dropdown-option-text">
              ${plan.displayed_text || ''}
            </span>
            ${plan.price_text ? `<span class="cart__sub-strip-dropdown-option-price">${plan.price_text}</span>` : ''}
            ${plan.badge_text ? `<span class="cart__sub-strip-dropdown-option-badge">${plan.badge_text}</span>` : ''}
          </button>
        `).join('')}
      </div>
    </div>`;
  },

  /**
   * Type A – Render the "swap to subscription" strip for a cart item.
   *
   * Single plan  -> clicking "Subscribe" swaps immediately.
   * Multiple plans -> clicking "Subscribe" expands the plan picker.
   */
  renderSubscriptionUpsellStrip(item, data) {
    if (!item || !data) return '';

    const disclaimerHtml = this._parseRichText(data.disclaimer);
    const thumbSrc = data.offer_image || (item.image ? item.image : '');

    const expandedHtml = `
      <div class="cart__sub-strip-expanded" data-subscription-expanded style="display:none;">
        ${this._renderPlanSelector(data.plans, thumbSrc)}
        ${disclaimerHtml ? `<div class="cart__sub-strip-disclaimer">${disclaimerHtml}</div>` : ''}
        <button class="cart__sub-strip-confirm-btn"
                data-subscription-confirm-swap
                data-cart-item-key="${item.key}"
                data-product-id="${item.product_id}"
                data-variant-id="${item.variant_id}"
                data-item-quantity="${item.quantity}">
          Start your plan
        </button>
      </div>`;

    return `
      <div class="cart__sub-strip cart__sub-strip--upsell"
           data-subscription-strip
           data-strip-type="upsell"
           data-product-id="${item.product_id}"
           data-variant-id="${item.variant_id}"
           data-cart-item-key="${item.key}"
           data-item-quantity="${item.quantity}">
        <div class="cart__sub-strip-collapsed">
          <div class="cart__sub-strip-message">
            ${this._subscriptionIcon()}
            <span>${data.offer_message || 'Start your SURI Plan & save'}</span>
          </div>
          <button class="cart__sub-strip-btn" data-subscription-expand>
            View plan
          </button>
        </div>
        ${expandedHtml}
      </div>`;
  },

  /**
   * Type B – Render the "add subscription addon" strip for a cart item.
   *
   * Always starts collapsed. Expanding shows product image, colour picker,
   * plan pills, disclaimer, and confirm button.
   */
  renderSubscriptionAddonStrip(item, data, allCartItems) {
    if (!item || !data) return '';

    const addonThumbSrc = data.addon_product_image || data.offer_image || (item.image ? item.image : '');

    const expandedHtml = `
      <div class="cart__sub-strip-expanded" data-subscription-expanded style="display:none;">
        ${this._renderPlanSelector(data.plans, addonThumbSrc)}
        ${data.disclaimer ? `<div class="cart__sub-strip-disclaimer">${this._parseRichText(data.disclaimer)}</div>` : ''}
        <button class="cart__sub-strip-confirm-btn"
                data-subscription-addon-add
                data-addon-product-id="${data.addon_product_id || ''}"
                data-parent-variant-id="${item.variant_id}">
          Start your plan
        </button>
      </div>`;

    return `
      <div class="cart__sub-strip cart__sub-strip--addon"
           data-subscription-strip
           data-strip-type="addon"
           data-product-id="${item.product_id}">
        <div class="cart__sub-strip-collapsed">
          <div class="cart__sub-strip-message">
            ${this._subscriptionIcon()}
            <span>${data.offer_message || 'Add a subscription'}</span>
          </div>
          <button class="cart__sub-strip-btn" data-subscription-expand>
            Add
          </button>
        </div>
        ${expandedHtml}
      </div>`;
  },

  // ---------------------------------------------------------------------------
  // Cart mutations: swap / add subscription items
  // ---------------------------------------------------------------------------

  /**
   * Type A – Swap a non-subscription item for its subscription counterpart.
   *
   * Steps:
   *  1. Resolve the matching variant on the subscription product (by colour).
   *  2. Remove the original item from cart.
   *  3. Add the subscription variant + selling plan.
   *  4. Re-render the cart.
   */
  async handleSubscriptionSwap(itemKey, variantId, productId, quantity, sellingPlanId, subscriptionProductId) {
    try {
      // Find the subscription product variants from the data
      const upsellData = window.subscriptionUpsells && window.subscriptionUpsells[productId];
      if (!upsellData) {
        console.error('[SubscriptionUpsell] No upsell data for product', productId);
        return;
      }

      // Find the plan that matches
      const plan = upsellData.plans.find(p => String(p.selling_plan_id) === String(sellingPlanId));
      if (!plan) {
        console.error('[SubscriptionUpsell] Plan not found for selling_plan_id', sellingPlanId);
        return;
      }

      // --- Variant resolution ---
      // Priority: 1) explicit subscription_variant_id, 2) auto-match by option values, 3) first available
      let targetVariantId = null;

      // 1) Explicit variant override from metaobject field
      if (plan.subscription_variant_id) {
        targetVariantId = plan.subscription_variant_id;
      }

      // 2) Auto-match by comparing original item's option values to subscription variants
      if (!targetVariantId && plan.subscription_product_variants && plan.subscription_product_variants.length > 0) {
        const originalItem = this.cart.items.find(i => i.key === itemKey);
        if (originalItem && originalItem.options_with_values) {
          const originalOptions = originalItem.options_with_values;
          const match = plan.subscription_product_variants.find(sv => {
            return sv.available && originalOptions.some(origOpt => {
              const origVal = (origOpt.value || '').toLowerCase();
              return [sv.option1, sv.option2, sv.option3].some(
                svOpt => svOpt && svOpt.toLowerCase() === origVal
              );
            });
          });
          if (match) targetVariantId = match.id;
        }
      }

      // 3) Fallback: first available variant
      if (!targetVariantId && plan.subscription_product_variants) {
        const fallback = plan.subscription_product_variants.find(v => v.available);
        if (fallback) targetVariantId = fallback.id;
      }

      if (!targetVariantId) {
        console.error('[SubscriptionUpsell] Could not resolve a target variant');
        return;
      }

      // Step 1: Remove original item
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });

      // Step 2: Add subscription variant with selling plan
      // Include _upsell_source_variant to link back to the original variant
      // so per-variant detection knows this swap already happened.
      // Also store the original color for cross-type addon detection.
      const swapProps = { _upsell_source_variant: String(variantId) };
      const originalItem = this.cart.items.find(i => i.key === itemKey);
      if (originalItem && originalItem.options_with_values) {
        const colorOpt = originalItem.options_with_values.find(o => /^colou?r$/i.test(o.name));
        if (colorOpt && colorOpt.value) swapProps._upsell_source_color = colorOpt.value;
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(targetVariantId),
            quantity: parseInt(quantity) || 1,
            selling_plan: parseInt(sellingPlanId),
            properties: swapProps
          }]
        })
      });

      // Step 3: Re-render cart
      this.updateCart();

    } catch (error) {
      console.error('[SubscriptionUpsell] Swap failed:', error);
    }
  },

  /**
   * Type B – Add a subscription addon product to the cart.
   *
   * Steps:
   *  1. Resolve the variant (by selected colour or first available).
   *  2. Remove any existing one-time counterpart of the addon product from
   *     the cart so the user is upgraded rather than duplicated.
   *  3. Add the subscription variant with the chosen selling plan.
   *  4. Re-render the cart.
   */
  async handleSubscriptionAddonAdd(stripEl) {
    try {
      const confirmBtn = stripEl.querySelector('[data-subscription-addon-add]');
      if (!confirmBtn) return;

      const addonProductId = confirmBtn.dataset.addonProductId;
      const parentVariantId = confirmBtn.dataset.parentVariantId;
      const parentProductId = stripEl.dataset.productId;

      // Get the addon data
      const addonData = window.subscriptionAddonUpsells && window.subscriptionAddonUpsells[parentProductId];
      if (!addonData) {
        console.error('[SubscriptionUpsell] No addon data for product', parentProductId);
        return;
      }

      // Get selected plan (from dropdown, single plan info, or fallback to first plan)
      const selectedPlan = stripEl.querySelector('.cart__sub-strip-dropdown-option.is-selected[data-subscription-plan-select]')
        || stripEl.querySelector('.cart__sub-strip-plan-info[data-subscription-plan-select]');
      const sellingPlanId = selectedPlan ? selectedPlan.dataset.sellingPlanId : (addonData.plans[0] && addonData.plans[0].selling_plan_id);
      const subscriptionProductId = selectedPlan ? selectedPlan.dataset.subscriptionProductId : (addonData.plans[0] && addonData.plans[0].subscription_product_id);
      const planQuantity = selectedPlan ? (parseInt(selectedPlan.dataset.planQuantity) || 1) : (addonData.plans[0] && addonData.plans[0].quantity || 1);

      if (!sellingPlanId) {
        console.error('[SubscriptionUpsell] No selling plan selected');
        return;
      }

      // Find the plan to get variant data
      const plan = addonData.plans.find(p => String(p.selling_plan_id) === String(sellingPlanId));
      if (!plan || !plan.subscription_product_variants) {
        console.error('[SubscriptionUpsell] Plan or variants not found');
        return;
      }

      // --- Variant resolution ---
      // Priority: 1) explicit subscription_variant_id, 2) auto-match by option values, 3) first available
      let targetVariantId = null;

      // 1) Explicit variant override from metaobject field
      if (plan.subscription_variant_id) {
        targetVariantId = plan.subscription_variant_id;
      }

      // 2) Auto-match: compare parent cart item's option values to subscription variants
      if (!targetVariantId && plan.subscription_product_variants && plan.subscription_product_variants.length > 0) {
        const parentItem = this.cart && this.cart.items
          ? this.cart.items.find(ci => String(ci.variant_id) === String(confirmBtn.dataset.parentVariantId))
          : null;

        if (parentItem && parentItem.options_with_values) {
          const parentOptions = parentItem.options_with_values;
          const match = plan.subscription_product_variants.find(sv => {
            return sv.available && parentOptions.some(origOpt => {
              const origVal = (origOpt.value || '').toLowerCase();
              return [sv.option1, sv.option2, sv.option3].some(
                svOpt => svOpt && svOpt.toLowerCase() === origVal
              );
            });
          });
          if (match) targetVariantId = match.id;
        }
      }

      // 3) Fallback: first available variant
      if (!targetVariantId && plan.subscription_product_variants) {
        const fallback = plan.subscription_product_variants.find(v => v.available);
        if (fallback) targetVariantId = fallback.id;
      }

      if (!targetVariantId) {
        console.error('[SubscriptionUpsell] Could not resolve target variant for addon');
        return;
      }

      // Step: upgrade existing one-time items to the new subscription.
      // Find any cart line that represents the one-time counterpart of what
      // we're adding (matches the addon's addon_product or one of its
      // subscription_products and has no selling plan) and remove it. When
      // the parent variant has a colour, only remove items that share that
      // colour (so a Sea Mist brush adding Sea Mist heads sub doesn't
      // remove an unrelated Forest heads single elsewhere in the cart).
      // Items without a colour (e.g. toothpaste) are removed regardless,
      // since they're a true duplicate of what we're about to add.
      const subscriptionProductIds = new Set(
        addonData.plans.map(p => String(p.subscription_product_id)).filter(Boolean)
      );
      const upgradeAddonProductId = addonData.addon_product_id
        ? String(addonData.addon_product_id)
        : null;

      let colourOptionName = (addonData.colour_match_option || '').toLowerCase();
      let parentColour = null;
      const parentItemForColour = this.cart && this.cart.items
        ? this.cart.items.find(ci => String(ci.variant_id) === String(parentVariantId))
        : null;
      if (parentItemForColour && parentItemForColour.options_with_values) {
        if (!colourOptionName) {
          const autoOpt = parentItemForColour.options_with_values.find(
            o => /^colou?r$/i.test(o.name)
          );
          if (autoOpt) colourOptionName = autoOpt.name.toLowerCase();
        }
        if (colourOptionName) {
          const matchOpt = parentItemForColour.options_with_values.find(
            o => o.name.toLowerCase() === colourOptionName
          );
          if (matchOpt) parentColour = matchOpt.value;
        }
      }

      const itemsToRemove = (this.cart && this.cart.items)
        ? this.cart.items.filter(ci => {
            if (ci.selling_plan_allocation) return false;
            const ciProductId = String(ci.product_id);
            const matchesProduct = (
              (upgradeAddonProductId && ciProductId === upgradeAddonProductId)
              || subscriptionProductIds.has(ciProductId)
            );
            if (!matchesProduct) return false;
            if (parentColour && ci.options_with_values) {
              const ciColourOpt = colourOptionName
                ? ci.options_with_values.find(o => o.name.toLowerCase() === colourOptionName)
                : ci.options_with_values.find(o => /^colou?r$/i.test(o.name));
              const ciColour = ciColourOpt ? ciColourOpt.value : null;
              if (ciColour && ciColour.toLowerCase() !== parentColour.toLowerCase()) {
                return false;
              }
            }
            return true;
          })
        : [];

      for (const ciToRemove of itemsToRemove) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: ciToRemove.key, quantity: 0 })
        });
      }

      // Add to cart (quantity from the plan's quantity field, e.g. 2-pack = 2)
      // Include hidden _addon_parent_variant property to link this addon to
      // the specific parent variant it was added for (enables per-variant detection).
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(targetVariantId),
            quantity: planQuantity,
            selling_plan: parseInt(sellingPlanId),
            properties: {
              _addon_parent_variant: parentVariantId
            }
          }]
        })
      });

      // Re-render cart
      this.updateCart();

    } catch (error) {
      console.error('[SubscriptionUpsell] Addon add failed:', error);
    }
  },

  // ---------------------------------------------------------------------------
  // Event binding: wire up subscription strip interactions
  // ---------------------------------------------------------------------------

  /**
   * Attach all event handlers for subscription upsell strips.
   * Called once after each cart re-render (from updateCart).
   */
  watchSubscriptionStrips() {
    const cartContainer = document.querySelector('[data-cart-content]');
    if (!cartContainer) return;

    // --- Expand / collapse toggle (animated) ---
    cartContainer.querySelectorAll('[data-subscription-expand]').forEach(btn => {
      if (!btn._originalLabel) btn._originalLabel = btn.textContent.trim();

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const strip = btn.closest('[data-subscription-strip]');
        if (!strip) return;
        const expanded = strip.querySelector('[data-subscription-expanded]');
        if (!expanded) return;

        const isOpen = strip.classList.contains('cart__sub-strip--open');

        if (isOpen) {
          btn.textContent = btn._originalLabel;
          expanded.style.overflow = 'hidden';
          expanded.style.maxHeight = expanded.scrollHeight + 'px';
          void expanded.offsetHeight;
          expanded.style.transition = 'max-height 0.3s ease, opacity 0.15s ease';
          expanded.style.maxHeight = '0';
          expanded.style.opacity = '0';
          strip.classList.remove('cart__sub-strip--open');
          expanded.addEventListener('transitionend', function handler(ev) {
            if (ev.propertyName !== 'max-height') return;
            expanded.removeEventListener('transitionend', handler);
            expanded.style.display = 'none';
            expanded.style.removeProperty('max-height');
            expanded.style.removeProperty('opacity');
            expanded.style.removeProperty('transition');
            expanded.style.removeProperty('overflow');
          });
        } else {
          btn.textContent = 'Close';
          expanded.style.overflow = 'hidden';
          expanded.style.display = 'block';
          expanded.style.transition = 'none';
          expanded.style.maxHeight = '0';
          expanded.style.opacity = '0';
          void expanded.offsetHeight;
          const target = expanded.scrollHeight;
          expanded.style.transition = 'max-height 0.3s ease, opacity 0.2s ease 0.05s';
          void expanded.offsetHeight;
          expanded.style.maxHeight = target + 'px';
          expanded.style.opacity = '1';
          strip.classList.add('cart__sub-strip--open');
          expanded.addEventListener('transitionend', function handler(ev) {
            if (ev.propertyName !== 'max-height') return;
            expanded.removeEventListener('transitionend', handler);
            expanded.style.removeProperty('max-height');
            expanded.style.removeProperty('transition');
            expanded.style.removeProperty('overflow');
          });
        }
      });
    });

    // --- Dropdown toggle (multi-plan) ---
    cartContainer.querySelectorAll('[data-subscription-dropdown-toggle]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const wrapper = trigger.closest('[data-subscription-dropdown]');
        if (!wrapper) return;
        const list = wrapper.querySelector('[data-subscription-dropdown-list]');
        if (!list) return;

        const isOpen = list.classList.contains('is-open');
        // Close all other dropdowns first
        cartContainer.querySelectorAll('[data-subscription-dropdown-list].is-open').forEach(l => l.classList.remove('is-open'));
        cartContainer.querySelectorAll('[data-subscription-dropdown-toggle].is-open').forEach(t => t.classList.remove('is-open'));

        if (!isOpen) {
          list.classList.add('is-open');
          trigger.classList.add('is-open');
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
      cartContainer.querySelectorAll('[data-subscription-dropdown-list].is-open').forEach(l => l.classList.remove('is-open'));
      cartContainer.querySelectorAll('[data-subscription-dropdown-toggle].is-open').forEach(t => t.classList.remove('is-open'));
    });

    // --- Plan option selection (dropdown or single info row) ---
    cartContainer.querySelectorAll('.cart__sub-strip-dropdown-option[data-subscription-plan-select]').forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const wrapper = option.closest('[data-subscription-dropdown]');
        if (!wrapper) return;

        // Update selected state
        wrapper.querySelectorAll('.cart__sub-strip-dropdown-option').forEach(o => o.classList.remove('is-selected'));
        option.classList.add('is-selected');

        // Update trigger label to match selected option
        const trigger = wrapper.querySelector('[data-subscription-dropdown-toggle]');
        if (trigger) {
          const label = trigger.querySelector('.cart__sub-strip-dropdown-label');
          if (label) {
            const plan = {
              badge_text: option.querySelector('.cart__sub-strip-dropdown-option-badge')?.textContent || '',
              displayed_text: (option.querySelector('.cart__sub-strip-dropdown-option-text')?.textContent || '').trim(),
              price_text: option.querySelector('.cart__sub-strip-dropdown-option-price')?.textContent || ''
            };
            label.innerHTML = `
              <span>${plan.displayed_text}</span>
              <span class="cart__sub-strip-plan-meta">
                ${plan.price_text ? `<span class="cart__sub-strip-plan-recurrence">${plan.price_text}</span>` : ''}
                ${plan.badge_text ? `<span class="cart__sub-strip-plan-badge-inline">${plan.badge_text}</span>` : ''}
              </span>
            `;
          }
        }

        // Close dropdown
        wrapper.querySelector('[data-subscription-dropdown-list]')?.classList.remove('is-open');
        trigger?.classList.remove('is-open');
      });
    });

    // --- Disclaimer links: open subscription modal via hash ---
    cartContainer.querySelectorAll('.cart__sub-strip-disclaimer a').forEach(link => {
      const href = (link.getAttribute('href') || '').toLowerCase();
      if (href === '#modal' || href.includes('modal')) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const hash = link.getAttribute('href') || '';
          const modalId = hash.replace(/^#/, '') || 'subscription-modal';
          if (window.openSubscriptionModal) {
            window.openSubscriptionModal(modalId);
          } else {
            const modal = document.querySelector('.subscription__modal[data-modal-id="' + modalId + '"]') || document.querySelector('.subscription__modal');
            if (modal) modal.classList.add('active');
          }
        });
      }
    });

    // --- Type A: Confirm swap (reads plan from info row, dropdown, or first plan) ---
    cartContainer.querySelectorAll('[data-subscription-confirm-swap]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const strip = btn.closest('[data-subscription-strip]');
        if (!strip) return;

        const selectedOption = strip.querySelector('.cart__sub-strip-dropdown-option.is-selected[data-subscription-plan-select]')
          || strip.querySelector('.cart__sub-strip-plan-info[data-subscription-plan-select]');
        if (!selectedOption) return;

        btn.disabled = true;
        btn.textContent = 'Adding\u2026';

        this.handleSubscriptionSwap(
          btn.dataset.cartItemKey,
          btn.dataset.variantId,
          btn.dataset.productId,
          parseInt(selectedOption.dataset.planQuantity) || 1,
          selectedOption.dataset.sellingPlanId,
          selectedOption.dataset.subscriptionProductId
        );
      });
    });

    // --- Type B: Confirm addon add ---
    cartContainer.querySelectorAll('[data-subscription-addon-add]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const strip = btn.closest('[data-subscription-strip]');
        if (!strip) return;

        btn.disabled = true;
        btn.textContent = 'Adding\u2026';

        this.handleSubscriptionAddonAdd(strip);
      });
    });
  },
};
